import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { displayName, marketUrl, fmtUsd, timeAgo } from "./lib/format.js";
import {
  normaliseAlert, normaliseTrader, normaliseTrade, filterVisibleAlerts,
  normalisePosition, normaliseClosedPosition, normalisePositionTrade,
} from "./lib/alerts.js";
import ValueChart from "./components/ValueChart.jsx";
import { TERMS_URL, PRIVACY_URL } from "./lib/links.js";
import { useAuthenticatedAlertStream } from "./lib/alertStream.js";
import {
  API_BASE,
  getStoredTokens, clearTokens,
  apiFetch, tryRefreshToken,
  authLogin, authRegister, authLogout, authForgotPassword,
} from "./lib/auth.js";

// ════════════════════════════════════════════════════════════════════
// Follow the Leader — Desktop Web App
//
// A fresh desktop-specific shell alongside the existing mobile App.jsx.
// Differences from mobile:
//   - Auth-gated: login/register required (same backend as iOS)
//   - JWT stored in localStorage, refresh token rotation handled here
//   - WebSocket sends ?token=<jwt> (new authenticated endpoint)
//   - Sidebar nav instead of bottom tab bar
//   - Right-panel trader detail (no full-page stack navigation)
//   - Subscription gate (Stripe checkout, same plans as iOS paywall)
//   - Wider layout: sidebar 220px + main content + optional detail panel
//
// ENDPOINTS USED:
//   POST /auth/register, /auth/login, /auth/refresh, /auth/logout
//   GET  /auth/me
//   POST /auth/forgot-password
//   POST /devices/register
//   GET  /devices/{id}/settings
//   PUT  /devices/{id}/settings
//   GET  /alerts?device_id=&limit=&conviction_min=&win_rate_min=&top_n=
//   GET  /leaderboard?window=day|week|all&limit=
//   GET  /traders/{address}/positions
//   GET  /traders/{address}/closed-positions
//   POST /payments/checkout
//   POST /payments/portal
//   POST /payments/cancel
//   POST /payments/resume
//   GET  /payments/status
//   WS   /ws?device_id=&token=<jwt>
// ════════════════════════════════════════════════════════════════════

const WS_BASE  = API_BASE.replace(/^http/, "ws");

// ── Stable device ID ──────────────────────────────────────────────────────────
function getDeviceId() {
  const key = "ftl_device_id";
  let id = localStorage.getItem(key);
  if (!id) { id = `web-${crypto.randomUUID()}`; localStorage.setItem(key, id); }
  return id;
}
const DEVICE_ID = getDeviceId();

// ── Design tokens ─────────────────────────────────────────────────────────────
// Dark Fintech — confirmed as the base style after a 5-way comparison pass.
// Green stays reserved for its semantic meaning (live/buy/positive PnL) —
// accentBlue/accentViolet are structural-only accents (nav, corner glows,
// badges) so they never compete with green's meaning elsewhere in the UI.
const C = {
  bg:          "#0B0F0D",
  sidebar:     "#0D1210",
  panel:       "#0F1411",
  panelHov:    "#131A16",
  panelEdge:   "#1A211D",
  panelEdge2:  "#222C26",
  green:       "#00D67E",
  greenDim:    "#0E2A1F",
  greenMid:    "#1B4030",
  sage:        "#7B8A82",
  sageDim:     "#4A5750",
  red:         "#FF5C5C",
  redDim:      "#2A1212",
  amber:       "#FFB648",
  amberDim:    "#1F1809",
  text:        "#EAF2EE",
  textDim:     "#A8B8B0",
  accentBlue:   "#3DB4FF",
  accentViolet: "#B26BFF",
};
const FONT     = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const MONO     = "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace";
const SIDEBAR_W = 220;
const DETAIL_W  = 380;

const RANK_MAX   = 200;
const RANK_CHIPS = [5, 10, 25, 50, 100, 200];

// ── Alert category tagging ────────────────────────────────────────────
// Fixed system thresholds mirroring apps/api/core.py's WHALE_CATEGORY_USD_FLOOR /
// DORMANCY_CATEGORY_DAYS — display-only, never a per-device gate.
const WHALE_CATEGORY_USD_FLOOR = 25000;
const DORMANCY_CATEGORY_DAYS   = 5.0;

// ── Mock data (fallback when API unreachable) ─────────────────────────────────
const MOCK_TRADERS = [
  { id:"0x9a4f2c8e1b7d3a6f9c0e4b2d8a1f7c3e6b9d0a2f", rank:1, pnlPeriod:184200, volPeriod:1240000, pnlDay:184200, pnlAll:2140000, winRate:0.71, resolved:42 },
  { id:"0x3e7b1d9a0c5f8e2b4d6a9c1f3e7b0d8a2c5f9e1b", rank:2, pnlPeriod:142800, volPeriod:980000,  pnlDay:142800, pnlAll:1820000, winRate:0.64, resolved:28 },
  { id:"0x1c8d4a2f6e9b0d3a7c5f1e8b2d4a6c9f0e3b7d5a", rank:3, pnlPeriod:96400,  volPeriod:610000,  pnlDay:96400,  pnlAll:410000,  winRate:0.58, resolved:7  },
  { id:"0x5f2e9c1a8d4b6f0e3c7a9d1f5b8e2c4a6d0f3b9e", rank:4, pnlPeriod:71900,  volPeriod:480000,  pnlDay:71900,  pnlAll:980000,  winRate:0.67, resolved:31 },
  { id:"0x8b3d6f1e9a2c4d7b0f5e8a1c3d6b9f2e4a7c0d5b", rank:5, pnlPeriod:53100,  volPeriod:340000,  pnlDay:53100,  pnlAll:220000,  winRate:0.55, resolved:19 },
  { id:"0x2a7c0e5b8d1f4a9c6e3b0d7f2a5c8e1b4d9f6a3c", rank:6, pnlPeriod:38700,  volPeriod:260000,  pnlDay:38700,  pnlAll:150000,  winRate:0.62, resolved:24 },
];
const NOW = Date.now();
const MOCK_ALERTS = [
  { txHash:"0xaa01", trader:MOCK_TRADERS[0].id, rank:1, winRate:0.71, resolved:42, side:"BUY",  outcome:"YES", market:"Will the Fed cut rates at the July 2026 meeting?",    slug:"fed-cut-july",        usd:48200, price:0.61, conviction:3.2, ts:NOW-90e3      },
  { txHash:"0xaa02", trader:MOCK_TRADERS[1].id, rank:2, winRate:0.64, resolved:28, side:"BUY",  outcome:"NO",  market:"Will BTC close above $120k this quarter?",           slug:"btc-120k-q3",         usd:22500, price:0.44, conviction:1.1, ts:NOW-14*60e3   },
  { txHash:"0xaa03", trader:MOCK_TRADERS[2].id, rank:3, winRate:0.58, resolved:7,  side:"SELL", outcome:"YES", market:"Will Team A win the championship?",                   slug:"team-a-championship",  usd:31000, price:0.55, conviction:2.4, ts:NOW-52*60e3   },
  { txHash:"0xaa04", trader:MOCK_TRADERS[1].id, rank:2, winRate:0.64, resolved:28, side:"BUY",  outcome:"YES", market:"Will inflation come in under 3% next month?",         slug:"inflation-under-3",   usd:12800, price:0.39, conviction:null,ts:NOW-3*3600e3  },
  { txHash:"0xaa05", trader:MOCK_TRADERS[3].id, rank:4, winRate:0.67, resolved:31, side:"BUY",  outcome:"YES", market:"Will the merger be approved by Q3?",                  slug:"merger-q3",           usd:67400, price:0.72, conviction:4.1, ts:NOW-6*3600e3  },
];
const MOCK_OPEN = [
  { id:"c1", market:"Will the Fed cut rates at the July 2026 meeting?", slug:"fed-cut-july",   outcome:"YES", currentValue:48200, positionSizeUsd:44000, price:0.61 },
  { id:"c2", market:"Will BTC close above $120k this quarter?",         slug:"btc-120k-q3",    outcome:"NO",  currentValue:22500, positionSizeUsd:19000, price:0.44 },
];
const MOCK_CLOSED = [
  { id:"r1", market:"Will inflation come in under 3% in June?",  slug:"inflation-under-3-june", outcome:"YES", pnl:31400,  positionSizeUsd:40000, won:true,  at:"2026-06-12" },
  { id:"r2", market:"Will the incumbent win the primary?",       slug:"incumbent-primary",       outcome:"NO",  pnl:-12800, positionSizeUsd:25000, won:false, at:"2026-06-08" },
  { id:"r3", market:"Will the merger be approved by Q2?",        slug:"merger-q2",               outcome:"YES", pnl:8600,   positionSizeUsd:18000, won:true,  at:"2026-05-30" },
];

// ── Data fetchers ─────────────────────────────────────────────────────────────
async function fetchAlerts({
  convictionMin, winRateMin, topN, rankWindow = "day", limit = 75,
  whaleUsdThreshold = 0, dormancyThresholdDays = 0, excludedCategories = "",
}) {
  const params = new URLSearchParams({
    device_id: DEVICE_ID, limit,
    conviction_min: convictionMin,
    win_rate_min:   winRateMin,
    top_n:          topN,
    rank_window:    rankWindow,
  });
  if (whaleUsdThreshold > 0)     params.set("whale_usd_threshold", whaleUsdThreshold);
  if (dormancyThresholdDays > 0) params.set("dormancy_threshold_days", dormancyThresholdDays);
  if (excludedCategories)        params.set("excluded_categories", excludedCategories);
  const { data, error } = await apiFetch(`/alerts?${params}`);
  if (error || !data) return { data: MOCK_ALERTS.map(normaliseAlert), fromMock: true };
  return { data: (data.alerts ?? data).map(normaliseAlert), fromMock: false };
}

// Dedup by id — prevents "two children with the same key" React warnings
// in the trader list (key={t.id}). Ranks can shift between the backend's
// paginated offset fetches (core.py's _fetch_leaderboard_page) for a
// fast-moving leaderboard, occasionally letting one trader land on two
// adjacent pages. iOS's fetchLeaderboard (lib/api.ts) already guards
// against this — this mirrors that fix for web.
function dedupeTraders(traders) {
  const seen = new Set();
  return traders.filter((t) => {
    if (!t.id || seen.has(t.id)) return false;
    seen.add(t.id);
    return true;
  });
}

async function fetchLeaderboard(window_ = "day") {
  const { data, error } = await apiFetch(`/leaderboard?window=${window_}&limit=200`);
  if (error || !data) return { data: MOCK_TRADERS.map(normaliseTrader), fromMock: true };
  return { data: dedupeTraders((data.traders ?? data).map(normaliseTrader)), fromMock: false };
}

async function fetchOpenPositions(address) {
  const { data, error } = await apiFetch(`/traders/${address}/positions`);
  if (error || !data) return { data: MOCK_OPEN, fromMock: true };
  return { data: (data.positions ?? data).map(normalisePosition), fromMock: false };
}

// win_rate/resolved are computed server-side from this same page of closed
// positions — /leaderboard always returns null/0 for those (see backend
// core.py), so this is the only place a real number comes from.
async function fetchClosedPositions(address) {
  const { data, error } = await apiFetch(`/traders/${address}/closed-positions`);
  if (error || !data) return { data: MOCK_CLOSED, winRate: null, resolved: 0, fromMock: true };
  return {
    data:     (data.positions ?? data).map(normaliseClosedPosition),
    winRate:  data.win_rate  ?? null,
    resolved: data.resolved  ?? 0,
    fromMock: false,
  };
}

// Buy/sell fill timeline for a single position — lazy-loaded when a closed
// position card is expanded.
async function fetchPositionTimeline(address, conditionId) {
  const { data, error } = await apiFetch(`/traders/${address}/positions/${conditionId}/trades`);
  if (error || !data) return { data: [], fromMock: true };
  return { data: (data.trades ?? data).map(normalisePositionTrade), fromMock: false };
}

async function fetchWalletValue(address) {
  const { data, error } = await apiFetch(`/traders/${address}/value`);
  if (error || !data) return { data: 0, fromMock: true };
  return { data: data.value ?? 0, fromMock: false };
}

async function fetchTradeHistory(address, { limit = 20, offset = 0, convictionMin = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (convictionMin > 0) params.set("conviction_min", convictionMin);
  const { data, error } = await apiFetch(`/traders/${address}/activity?${params}`);
  if (error || !data) return { data: [], fromMock: true, hasMore: false };
  const trades = (data.trades ?? data).map(normaliseTrade);
  // has_more comes from the server (based on the raw, pre-filter page size),
  // not trades.length === limit — the conviction filter can legitimately
  // shrink a full raw page to fewer (or zero) qualifying trades.
  return { data: trades, fromMock: false, hasMore: data.has_more ?? false };
}

async function fetchValueHistory(address, days = 30) {
  const { data, error } = await apiFetch(`/traders/${address}/value-history?days=${days}`);
  if (error || !data) return { data: [], fromMock: true };
  return { data: data.points ?? data, fromMock: false };
}

async function fetchDeviceSettings() {
  const { data } = await apiFetch(`/devices/${DEVICE_ID}/settings`);
  return data;
}

async function saveDeviceSettings(patch) {
  return apiFetch(`/devices/${DEVICE_ID}/settings`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

async function registerDevice() {
  await apiFetch("/devices/register", {
    method: "POST",
    body: JSON.stringify({ device_id: DEVICE_ID, platform: "web" }),
  });
}

async function fetchSubscriptionStatus() {
  const { data } = await apiFetch("/payments/status");
  return data;
}

// Real prices from Stripe via the backend, so the paywall can't show a
// different price than Checkout charges. null on failure: the paywall then
// shows plans without prices rather than possibly-wrong ones.
async function fetchPlans() {
  const { data, error } = await apiFetch("/payments/plans");
  if (error || !data?.plans) return null;
  return data.plans;
}

async function startCheckout(plan) {
  const { data, error } = await apiFetch("/payments/checkout", {
    method: "POST",
    body: JSON.stringify({ plan, device_id: DEVICE_ID }),
  });
  if (error || !data?.url) return { error: error ?? "Checkout unavailable" };
  return { url: data.url };
}

async function openBillingPortal() {
  const { data, error } = await apiFetch("/payments/portal", { method: "POST" });
  if (error || !data?.url) return { error };
  return { url: data.url };
}

// Cancels at the end of the current billing period (no proration refund) —
// access continues until current_period_end.
async function cancelSubscription() {
  const { data, error } = await apiFetch("/payments/cancel", { method: "POST" });
  if (error || !data) return { error: error ?? "Cancel failed" };
  return { data };
}

// Undoes a pending cancellation before the current period ends.
async function resumeSubscription() {
  const { data, error } = await apiFetch("/payments/resume", { method: "POST" });
  if (error || !data) return { error: error ?? "Resume failed" };
  return { data };
}

// ══════════════════════════════════════════════════════════════════════════════
// WEBSOCKET HOOK — authenticated, back-off reconnect
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// SHARED UI ATOMS
// ══════════════════════════════════════════════════════════════════════════════
function Spinner({ size = 24 }) {
  return (
    <div style={{ display:"flex", justifyContent:"center", padding:`${size}px 0` }}>
      <div style={{ width:size, height:size, border:`2px solid ${C.panelEdge2}`, borderTopColor:C.green, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
    </div>
  );
}

function OutcomePill({ o }) {
  const yes = o === "YES";
  return (
    <span style={{ fontSize:10.5, fontWeight:700, color:yes?C.green:C.red, background:yes?C.greenDim:C.redDim, border:`1px solid ${(yes?C.green:C.red)}44`, borderRadius:5, padding:"1px 6px" }}>{o}</span>
  );
}

function WinRate({ winRate, resolved, showSmall = true }) {
  if (winRate == null) return null;
  const pct   = Math.round(winRate * 100);
  const small = resolved < 10;
  return (
    <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>
      <span style={{ color: pct >= 60 ? C.green : C.textDim }}>{pct}% win</span>
      {small && showSmall && <span title={`Only ${resolved} resolved — small sample.`} style={{ color:C.amber, fontSize:10, fontWeight:600, cursor:"help" }}>small sample</span>}
    </span>
  );
}

function MarketLink({ slug, eventSlug, children, color = C.sage }) {
  const url = marketUrl(slug, eventSlug);
  if (!url) return <span>{children}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color, textDecoration:"none" }}>
      {children}<span style={{ color:C.sageDim, marginLeft:4, fontSize:11 }} aria-hidden="true">↗</span>
    </a>
  );
}

function Badge({ children, title, bg, bd, fg, bold }) {
  return (
    <span title={title} style={{ background:bg, border:`1px solid ${bd}`, color:fg, fontSize:11.5, fontWeight:bold?700:600, padding:"2px 8px", borderRadius:7, whiteSpace:"nowrap" }}>{children}</span>
  );
}

function Note({ children, tone = "amber" }) {
  const col = tone === "green" ? C.green : tone === "red" ? C.red : C.amber;
  const bg  = tone === "green" ? C.greenDim : tone === "red" ? C.redDim : C.amberDim;
  return <div style={{ fontSize:12.5, color:col, background:bg, border:`1px solid ${col}33`, borderRadius:10, padding:"9px 12px", lineHeight:1.45 }}>{children}</div>;
}

function Card({ children, style }) {
  return <div style={{ background:C.panel, border:`1px solid ${C.panelEdge}`, borderRadius:14, padding:18, ...style }}>{children}</div>;
}

function Divider({ style }) {
  return <div style={{ height:1, background:C.panelEdge, margin:"16px -18px", ...style }} />;
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:11, color:C.sage, marginBottom:3, letterSpacing:0.3, textTransform:"uppercase" }}>{label}</div>
      <div style={{ fontSize:17, fontWeight:700, color:color??C.text }}>{value}</div>
    </div>
  );
}

function PrimaryButton({ children, disabled, onClick, loading: isLoading, style }) {
  return (
    <button
      onClick={() => !disabled && !isLoading && onClick()}
      disabled={disabled || isLoading}
      style={{ padding:"12px 24px", borderRadius:12, border:"none", fontSize:14.5, fontWeight:600, cursor:disabled||isLoading?"not-allowed":"pointer", background:disabled||isLoading?C.greenDim:C.green, color:disabled||isLoading?C.sageDim:C.bg, transition:"background 140ms", display:"flex", alignItems:"center", justifyContent:"center", gap:8, ...style }}
    >
      {isLoading && <div style={{ width:14, height:14, border:`2px solid ${C.sageDim}`, borderTopColor:C.sage, borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />}
      {children}
    </button>
  );
}

// Confirmation dialog before cancelling — cancels at the end of the current
// billing period (see /payments/cancel), not immediately, so the copy has
// to be explicit that access continues and there's no refund.
function CancelSubscriptionModal({ periodEndLabel, onConfirm, onDismiss }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      await onConfirm();
      // On success the caller closes the modal — no setLoading(false) here,
      // so the button doesn't flash back to enabled before it unmounts.
    } catch (e) {
      setError(e?.message ?? "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}
      onClick={() => !loading && onDismiss()}
    >
      <div
        style={{ background:C.panel, border:`1px solid ${C.panelEdge}`, borderRadius:16, padding:24, width:380, maxWidth:"90vw" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize:18, fontWeight:700, marginBottom:8 }}>Cancel subscription?</div>
        <div style={{ fontSize:14, color:C.sage, lineHeight:1.5, marginBottom:20 }}>
          You'll keep full access until <span style={{ color:C.text, fontWeight:600 }}>{periodEndLabel}</span>.
          No refund for the current billing period — you can resume anytime before then.
        </div>
        {error && <div style={{ fontSize:12.5, color:C.red, marginBottom:12 }}>{error}</div>}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <PrimaryButton onClick={handleConfirm} loading={loading} style={{ background:C.redDim, color:C.red }}>
            Cancel subscription
          </PrimaryButton>
          <button
            onClick={onDismiss}
            disabled={loading}
            style={{ background:"transparent", border:"none", color:C.sage, fontSize:14, fontWeight:600, padding:"10px 0", cursor:loading?"not-allowed":"pointer" }}
          >
            Keep subscription
          </button>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, type = "text", value, onChange, placeholder, error, autoFocus }) {
  return (
    <div style={{ marginBottom:16 }}>
      {label && <div style={{ fontSize:12.5, color:C.sage, marginBottom:6, fontWeight:500 }}>{label}</div>}
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} autoFocus={autoFocus}
        style={{ width:"100%", background:C.bg, border:`1px solid ${error ? C.red+"66" : C.panelEdge2}`, borderRadius:10, color:C.text, fontSize:15, padding:"10px 14px", outline:"none", boxSizing:"border-box", transition:"border-color 120ms" }}
        onFocus={(e) => e.target.style.borderColor = error ? C.red : C.green+"88"}
        onBlur={(e) => e.target.style.borderColor = error ? C.red+"66" : C.panelEdge2}
      />
      {error && <div style={{ fontSize:12, color:C.red, marginTop:5 }}>{error}</div>}
    </div>
  );
}

function MockBanner() {
  return (
    <div style={{ background:C.amberDim, border:`1px solid ${C.amber}44`, borderRadius:10, padding:"8px 14px", marginBottom:14, fontSize:12.5, color:C.amber, display:"flex", alignItems:"center", gap:8 }}>
      <span>⚠</span>
      <span>Backend unreachable — showing mock data. Check that the API is running at <code style={{ fontSize:11 }}>{API_BASE}</code>.</span>
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ padding:"40px 24px", textAlign:"center", color:C.sage, fontSize:14.5, lineHeight:1.6, background:C.panel, border:`1px solid ${C.panelEdge}`, borderRadius:14 }}>{children}</div>;
}

function SectionLabel({ children, color = C.sage }) {
  return <div style={{ fontSize:11, fontWeight:600, letterSpacing:0.7, textTransform:"uppercase", color, marginBottom:10 }}>{children}</div>;
}

function Pills({ value, onChange, options, dimLast }) {
  return (
    <div style={{ display:"flex", gap:6 }}>
      {options.map(([k, label], i) => {
        const active = value === k, dim = dimLast && i === options.length-1;
        return (
          <button key={k} onClick={() => onChange(k)} style={{ padding:"7px 16px", borderRadius:9, fontSize:13, fontWeight:600, cursor:"pointer", border:`1px solid ${active?C.green+"66":C.panelEdge}`, background:active?C.greenDim:"transparent", color:active?C.green:dim?C.sageDim:C.sage }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function Logo({ size = 36 }) {
  return <img src="/brand/ftl-mark-onDark.svg" alt="" width={size} height={size} style={{ flexShrink:0 }} />;
}

function TraderAvatar({ id, size = 32 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:Math.round(size*0.28), background:C.greenDim, border:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:Math.round(size*0.38), fontWeight:700, color:C.green, flexShrink:0, fontFamily:MONO }}>
      {(id ?? "0x").slice(0,2)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// AUTH SCREENS
// ══════════════════════════════════════════════════════════════════════════════
function AuthShell({ children }) {
  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FONT }}>
      <div style={{ width:"100%", maxWidth:420, padding:"0 24px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:40 }}>
          <div style={{ position:"relative", display:"flex" }}>
            <Logo size={36} />
            <span style={{ position:"absolute", inset:-4, borderRadius:10, border:`1.5px solid ${C.green}`, animation:"ftlp 2.4s ease-out infinite" }} />
          </div>
          <span style={{ fontSize:17, fontWeight:600, letterSpacing:-0.2, fontFamily:MONO }}>Follow the Leader</span>
        </div>
        {children}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, onSwitchToRegister, kickedMessage }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async () => {
    if (!email || !password) return;
    setLoading(true); setError("");
    const { error: err } = await authLogin(email.trim().toLowerCase(), password);
    setLoading(false);
    if (err) { setError(err); return; }
    onLogin();
  };

  const sendForgot = async () => {
    if (!email) { setError("Enter your email first."); return; }
    setLoading(true);
    await authForgotPassword(email.trim().toLowerCase());
    setLoading(false);
    setForgotSent(true);
  };

  if (forgotSent) {
    return (
      <AuthShell>
        <h1 style={{ fontSize:28, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px", color:C.text }}>Check your inbox</h1>
        <p style={{ color:C.sage, fontSize:15, lineHeight:1.55, marginBottom:28 }}>If an account exists for <strong style={{ color:C.text }}>{email}</strong>, we've sent a reset link.</p>
        <button onClick={() => { setForgotSent(false); setShowForgot(false); }} style={{ background:"transparent", border:"none", color:C.green, cursor:"pointer", fontSize:14.5, fontWeight:500, padding:0 }}>← Back to login</button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 style={{ fontSize:28, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px", color:C.text }}>{showForgot ? "Reset password" : "Welcome back"}</h1>
      <p style={{ color:C.sage, fontSize:15, lineHeight:1.55, marginBottom:28 }}>
        {showForgot ? "We'll email you a reset link." : "Sign in to Follow the Leader."}
      </p>

      {kickedMessage && <Note tone="amber" style={{ marginBottom:16 }}>⚠ {kickedMessage}</Note>}
      {error && <Note tone="red" style={{ marginBottom:16 }}>{error}</Note>}

      <InputField label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
      {!showForgot && (
        <InputField label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••"
          error={error && !email ? " " : undefined} />
      )}

      <PrimaryButton
        onClick={showForgot ? sendForgot : submit}
        loading={loading}
        disabled={showForgot ? !email : (!email || !password)}
        style={{ width:"100%", marginTop:4 }}
      >
        {showForgot ? "Send reset link" : "Sign in"}
      </PrimaryButton>

      <div style={{ display:"flex", justifyContent:"space-between", marginTop:20, fontSize:14 }}>
        <button onClick={() => setShowForgot(!showForgot)} style={{ background:"none", border:"none", color:C.sage, cursor:"pointer", padding:0, fontSize:14 }}>
          {showForgot ? "← Back" : "Forgot password?"}
        </button>
        <button onClick={onSwitchToRegister} style={{ background:"none", border:"none", color:C.green, cursor:"pointer", padding:0, fontSize:14, fontWeight:500 }}>
          Create account →
        </button>
      </div>
    </AuthShell>
  );
}

function RegisterScreen({ onRegister, onSwitchToLogin }) {
  const [email, setEmail]     = useState("");
  const [pass, setPass]       = useState("");
  const [pass2, setPass2]     = useState("");
  const [ack, setAck]         = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors]   = useState({});

  const validate = () => {
    const e = {};
    if (!email || !/\S+@\S+\.\S+/.test(email)) e.email = "Enter a valid email address.";
    if (pass.length < 8)  e.pass  = "Password must be at least 8 characters.";
    if (pass !== pass2)   e.pass2 = "Passwords don't match.";
    if (!ack)             e.ack   = "You must acknowledge to continue.";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true); setErrors({});
    const { error: err } = await authRegister(email.trim().toLowerCase(), pass);
    setLoading(false);
    if (err) { setErrors({ form: err }); return; }
    onRegister();
  };

  return (
    <AuthShell>
      <h1 style={{ fontSize:28, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px", color:C.text }}>Create your account</h1>
      <p style={{ color:C.sage, fontSize:15, lineHeight:1.55, marginBottom:24 }}>One account works across web and iOS.</p>

      {errors.form && <Note tone="red" style={{ marginBottom:16 }}>{errors.form}</Note>}

      <InputField label="Email" type="email" value={email} onChange={setEmail} error={errors.email} autoFocus />
      <InputField label="Password" type="password" value={pass} onChange={setPass} error={errors.pass} placeholder="At least 8 characters" />
      <InputField label="Confirm password" type="password" value={pass2} onChange={setPass2} error={errors.pass2} />

      <div style={{ border:`1px solid ${C.amber}44`, background:C.amberDim, borderRadius:12, padding:16, marginBottom:18 }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.amber, marginBottom:8 }}>Not financial advice</div>
        <p style={{ fontSize:13, lineHeight:1.55, color:C.sage, margin:"0 0 12px" }}>Follow the Leader shows public trading activity for informational purposes only. It never places trades. Prediction markets carry real risk of loss.</p>
        <label style={{ display:"flex", gap:10, alignItems:"flex-start", cursor:"pointer", fontSize:13.5, lineHeight:1.4, color: errors.ack ? C.red : C.text }}>
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ width:18, height:18, marginTop:1, accentColor:C.green, flexShrink:0 }} />
          <span>I understand this isn't financial advice and trading involves risk.</span>
        </label>
        {errors.ack && <div style={{ fontSize:12, color:C.red, marginTop:6 }}>{errors.ack}</div>}
      </div>

      <PrimaryButton onClick={submit} loading={loading} style={{ width:"100%" }}>Create account</PrimaryButton>

      <div style={{ textAlign:"center", marginTop:20, fontSize:14 }}>
        <span style={{ color:C.sage }}>Already have an account? </span>
        <button onClick={onSwitchToLogin} style={{ background:"none", border:"none", color:C.green, cursor:"pointer", padding:0, fontSize:14, fontWeight:500 }}>Sign in</button>
      </div>
      <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:20, lineHeight:1.5 }}>
        Not affiliated with or endorsed by Polymarket.
      </p>
    </AuthShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUBSCRIPTION GATE
// ══════════════════════════════════════════════════════════════════════════════
function fmtDate(epochSeconds) {
  if (!epochSeconds) return "—";
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}

const PLANS = [
  { key:"monthly", label:"Monthly", badge:"Most Popular" },
  { key:"annual",  label:"Annual",  badge:null },
  { key:"weekly",  label:"Weekly",  badge:null },
];

const PER = { week: "wk", month: "mo", year: "yr" };

function fmtMoney(amount, currency) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(amount / 100);
}

// "Save 22%" for annual vs. twelve months of monthly, computed from the real
// prices so the badge can't overstate the saving.
function annualSavings(prices) {
  const m = prices.monthly, a = prices.annual;
  if (!m || !a || m.interval !== "month" || a.interval !== "year" || m.currency !== a.currency) return null;
  const pct = Math.round((1 - a.amount / (m.amount * 12)) * 100);
  return pct > 0 ? `Save ${pct}%` : null;
}

// trialEligible comes from /payments/status: a free trial is only for an
// account's first subscription, so returning subscribers aren't promised one.
function PaywallScreen({ email, onLogout, checkoutCancelled, trialEligible = true }) {
  const [loading, setLoading] = useState(null);
  const [error, setError]     = useState("");
  const [prices, setPrices]   = useState(null); // { weekly: {...}, ... } once loaded

  useEffect(() => {
    fetchPlans().then((plans) => {
      if (plans) setPrices(Object.fromEntries(plans.map((p) => [p.id, p])));
    });
  }, []);
  const savings = prices ? annualSavings(prices) : null;

  const checkout = async (plan) => {
    setLoading(plan); setError("");
    const { url, error: err } = await startCheckout(plan);
    if (err || !url) { setError(err ?? "Checkout unavailable — try again."); setLoading(null); return; }
    window.location.href = url;
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FONT, padding:"40px 24px" }}>
      <div style={{ width:"100%", maxWidth:560 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:40 }}>
          <Logo size={36} />
          <span style={{ fontSize:17, fontWeight:600, letterSpacing:-0.2, fontFamily:MONO }}>Follow the Leader</span>
        </div>

        <h1 style={{ fontSize:32, fontWeight:700, letterSpacing:-0.8, margin:"0 0 10px" }}>Know when the leaders move.</h1>
        <p style={{ fontSize:16, lineHeight:1.55, color:C.sage, margin:"0 0 32px" }}>Real-time alerts when a top Polymarket trader opens an unusually large position — with conviction scoring so you know which trades actually matter.</p>

        {checkoutCancelled && !error && <Note tone="amber" style={{ marginBottom:20 }}>Checkout canceled. You haven't been charged.</Note>}
        {error && <Note tone="red" style={{ marginBottom:20 }}>{error}</Note>}

        <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
          {PLANS.map((plan) => {
            const price = prices?.[plan.key];
            const badge = plan.key === "annual" ? savings : plan.badge;
            const trialDays = trialEligible ? price?.trial_days : 0;
            const trial = !price ? " "
              : trialDays ? `${trialDays}-day free trial` : "No trial · cancel any time";
            const p = { ...plan, badge, trial };
            return (
            <div key={p.key} style={{ background:C.panel, border:`1px solid ${p.key==="monthly"?C.green+"44":C.panelEdge}`, borderRadius:14, padding:"18px 20px", display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:16, fontWeight:700 }}>{p.label}</span>
                  {p.badge && <span style={{ fontSize:11, fontWeight:700, color:p.key==="annual"?C.amber:C.green, background:p.key==="annual"?C.amberDim:C.greenDim, border:`1px solid ${p.key==="annual"?C.amber+"44":C.green+"44"}`, borderRadius:6, padding:"2px 8px" }}>{p.badge}</span>}
                </div>
                <div style={{ fontSize:13, color:C.sage }}>{p.trial}</div>
                {p.note && <div style={{ fontSize:12, color:C.sageDim, marginTop:3 }}>{p.note}</div>}
              </div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <div style={{ fontSize:17, fontWeight:700, marginBottom:6 }}>
                  {price ? `${fmtMoney(price.amount, price.currency)}/${PER[price.interval] ?? price.interval}` : "\u00a0"}
                </div>
                <PrimaryButton onClick={() => checkout(p.key)} loading={loading === p.key}
                  style={{ padding:"8px 18px", fontSize:13.5 }}>
                  {trialDays ? "Start free trial" : "Start now"}
                </PrimaryButton>
              </div>
            </div>
            );
          })}
        </div>

        <p style={{ fontSize:12, color:C.sageDim, lineHeight:1.6, margin:"0 0 24px" }}>
          Subscriptions renew automatically at the price shown until you cancel. With a free trial,
          you're charged when the trial ends unless you cancel before then. Cancel any time in
          Settings → Subscription; you keep access until the end of the period you've paid for.
          Payments are processed securely by Stripe.{" "}
          <a href={TERMS_URL} target="_blank" rel="noreferrer" style={{ color:C.sage }}>Terms</a>
          {" · "}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ color:C.sage }}>Privacy</a>
        </p>

        <p style={{ fontSize:12, color:C.sageDim, textAlign:"center", lineHeight:1.6 }}>
          Signed in as <strong style={{ color:C.textDim }}>{email}</strong> ·{" "}
          <button onClick={onLogout} style={{ background:"none", border:"none", color:C.sage, cursor:"pointer", fontSize:12, padding:0, textDecoration:"underline" }}>Sign out</button>
        </p>
        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:8 }}>
          Not financial advice · Not affiliated with or endorsed by Polymarket
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ONBOARDING
// ══════════════════════════════════════════════════════════════════════════════
const ONBOARDING_FEATURES = [
  ["Live alerts",           "Fresh trades, not stale leaderboard data.",           0.45],
  ["Conviction scoring",    "See how a bet compares to a trader's usual size.",    0.80],
  ["Follow your favorites", "Track specific traders, leaderboard or not.",         0.60],
];

function OnboardingIntroScreen({ onContinue }) {
  const [ack, setAck] = useState(false);
  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FONT, padding:"40px 24px" }}>
      <div style={{ width:"100%", maxWidth:480 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36 }}>
          <Logo size={36} />
          <span style={{ fontSize:17, fontWeight:600, letterSpacing:-0.2, fontFamily:MONO }}>Follow the Leader</span>
        </div>

        <h1 style={{ fontSize:34, lineHeight:1.08, fontWeight:700, letterSpacing:-0.9, margin:"0 0 12px" }}>Know when the leaders move.</h1>
        <p style={{ fontSize:16, lineHeight:1.5, color:C.sage, margin:"0 0 30px" }}>Get notified the moment a top Polymarket trader opens an unusually large position.</p>

        <div style={{ display:"flex", flexDirection:"column", gap:18, marginBottom:26 }}>
          {ONBOARDING_FEATURES.map(([title, body, fill]) => (
            <div key={title} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              <div style={{ width:40, height:40, borderRadius:11, background:C.greenDim, border:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"flex-end", justifyContent:"center", padding:7, flexShrink:0 }}>
                <div style={{ width:6, height:`${fill*100}%`, borderRadius:3, background:C.green, boxShadow:`0 0 8px ${C.green}66` }} />
              </div>
              <div>
                <div style={{ fontSize:15.5, fontWeight:600, marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:14, color:C.sage, lineHeight:1.4 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ border:`1px solid ${C.amber}44`, background:C.amberDim, borderRadius:14, padding:18, marginBottom:20 }}>
          <div style={{ color:C.amber, fontWeight:600, fontSize:15, marginBottom:10 }}>Not financial advice</div>
          <p style={{ fontSize:13.5, lineHeight:1.55, color:C.sage, margin:"0 0 14px" }}>This app shows public trading activity for informational purposes only. It does not recommend trades. Prediction markets carry real risk of loss, and past activity does not predict future results.</p>
          <label style={{ display:"flex", gap:12, alignItems:"flex-start", cursor:"pointer", fontSize:14, lineHeight:1.4 }}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ width:20, height:20, marginTop:1, accentColor:C.green, flexShrink:0, cursor:"pointer" }} />
            <span>I understand this isn't financial advice and trading involves risk.</span>
          </label>
        </div>

        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginBottom:20 }}>Not affiliated with or endorsed by Polymarket.</p>

        <PrimaryButton disabled={!ack} onClick={onContinue} style={{ width:"100%" }}>Continue</PrimaryButton>
      </div>
    </div>
  );
}

function OnboardingPrimingScreen({ onContinue, sampleAlert }) {
  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FONT, padding:"40px 24px" }}>
      <div style={{ width:"100%", maxWidth:480 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36 }}>
          <Logo size={36} />
          <span style={{ fontSize:17, fontWeight:600, letterSpacing:-0.2, fontFamily:MONO }}>Follow the Leader</span>
        </div>

        <h1 style={{ fontSize:32, lineHeight:1.1, fontWeight:700, letterSpacing:-0.8, margin:"0 0 12px" }}>See what an alert looks like.</h1>
        <p style={{ fontSize:16, lineHeight:1.5, color:C.sage, margin:"0 0 24px" }}>We'll only ping you when a leader makes a real move — a position well above their usual size. Here's an example:</p>

        <div style={{ marginBottom:24 }}>
          <AlertCard a={sampleAlert} sample read={false} onRead={() => {}} />
        </div>

        <div style={{ display:"flex", gap:12, alignItems:"flex-start", background:C.panel, border:`1px solid ${C.panelEdge}`, borderRadius:14, padding:"14px 16px", marginBottom:28 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:C.green, marginTop:7, flexShrink:0, boxShadow:`0 0 8px ${C.green}88` }} />
          <p style={{ fontSize:13.5, lineHeight:1.5, color:C.sage, margin:0 }}>Alerts stream in live over your connection and show up right in the app. Tune sensitivity and quiet hours anytime in settings.</p>
        </div>

        <PrimaryButton onClick={onContinue} style={{ width:"100%" }}>Get started</PrimaryButton>
      </div>
    </div>
  );
}

const WALKTHROUGH_STEPS = 5;

function WalkthroughLegendRow({ icon, title, body }) {
  return (
    <div style={{ display:"flex", gap:14, alignItems:"flex-start", marginBottom:16 }}>
      <div style={{ width:32, height:32, borderRadius:9, background:C.greenDim, border:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:14, color:C.green }}>
        {icon ?? "•"}
      </div>
      <div>
        <div style={{ fontSize:14.5, fontWeight:600, marginBottom:2 }}>{title}</div>
        <div style={{ fontSize:13, color:C.sage, lineHeight:1.4 }}>{body}</div>
      </div>
    </div>
  );
}

function WalkthroughScreen({ onDone, sampleAlert }) {
  const [step, setStep] = useState(0);
  const last = step === WALKTHROUGH_STEPS - 1;
  const next = () => (last ? onDone() : setStep((s) => s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:FONT, padding:"40px 24px" }}>
      <div style={{ width:"100%", maxWidth:480 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28 }}>
          <div style={{ display:"flex", gap:6 }}>
            {Array.from({ length: WALKTHROUGH_STEPS }).map((_, i) => (
              <div key={i} style={{ width:7, height:7, borderRadius:4, background: i === step ? C.green : C.panelEdge }} />
            ))}
          </div>
          <span onClick={onDone} style={{ fontSize:13.5, fontWeight:500, color:C.sage, cursor:"pointer" }}>Skip</span>
        </div>

        {step === 0 && (
          <>
            <div style={{ width:52, height:52, borderRadius:15, background:C.greenDim, border:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:C.green, marginBottom:22 }}>⚡</div>
            <h1 style={{ fontSize:28, lineHeight:1.1, fontWeight:700, letterSpacing:-0.7, margin:"0 0 14px" }}>Know when the leaders move.</h1>
            <p style={{ fontSize:15, lineHeight:1.5, color:C.sage, margin:0 }}>
              Follow the Leader watches the top traders on Polymarket and alerts you the moment
              one of them opens an unusually large or high-conviction position. It's read-only —
              we never place trades for you.
            </p>
          </>
        )}

        {step === 1 && (
          <>
            <h1 style={{ fontSize:24, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px" }}>Reading an alert</h1>
            <p style={{ fontSize:14, color:C.sage, margin:"0 0 20px" }}>Every card packs in a lot — here's what each piece means.</p>
            <div style={{ marginBottom:20 }}>
              <AlertCard a={sampleAlert} sample read={false} onRead={() => {}} />
            </div>
            <WalkthroughLegendRow title="#1" body="Their rank on today's leaderboard." />
            <WalkthroughLegendRow title="71% win" body={`Win rate on their recently resolved markets — flagged "small sample" when there isn't much history yet.`} />
            <WalkthroughLegendRow title="3.2× usual" body="This trade is that many times bigger than their typical position size." />
            <WalkthroughLegendRow title="Bought YES" body="The side and outcome of the position." />
            <WalkthroughLegendRow title="$48,200 @ 0.61" body="Trade size and entry price." />
            <WalkthroughLegendRow title="→ $79,016 to win" body="What they'd collect if this resolves in their favor." />
            <WalkthroughLegendRow title="market ↗" body="Opens the live market on Polymarket." />
          </>
        )}

        {step === 2 && (
          <>
            <h1 style={{ fontSize:24, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px" }}>Whale & Dormant Wallet tabs</h1>
            <p style={{ fontSize:14, color:C.sage, margin:"0 0 20px" }}>Two dedicated feeds, always on — independent of your regular filters.</p>
            <WalkthroughLegendRow icon="◆" title="Whale" body="Any trade over your whale threshold, regardless of conviction." />
            <WalkthroughLegendRow icon="☾" title="Dormant Wallet" body="A trader who'd gone quiet suddenly trades again." />
            <p style={{ fontSize:13, color:C.sage, lineHeight:1.5, marginTop:6 }}>
              These bypass your conviction and win-rate settings entirely, so you never miss one.
            </p>
          </>
        )}

        {step === 3 && (
          <>
            <h1 style={{ fontSize:24, fontWeight:700, letterSpacing:-0.5, margin:"0 0 10px" }}>Tuning your alerts</h1>
            <p style={{ fontSize:14, color:C.sage, margin:"0 0 20px" }}>Settings controls exactly what reaches your main Alerts feed:</p>
            <WalkthroughLegendRow title="Conviction multiplier" body="How many times bigger than usual a trade must be." />
            <WalkthroughLegendRow title="Minimum win rate" body="Filter out traders below a win-rate floor." />
            <WalkthroughLegendRow title="Whale $ threshold" body="Alert on any trade over a dollar amount." />
            <WalkthroughLegendRow title="Dormancy days" body="How long a trader must go quiet before reactivating counts." />
            <p style={{ fontSize:13, color:C.sage, lineHeight:1.5, marginTop:6 }}>
              You can change any of these anytime — nothing here is permanent.
            </p>
          </>
        )}

        {step === 4 && (
          <>
            <h1 style={{ fontSize:24, fontWeight:700, letterSpacing:-0.5, margin:"0 0 18px" }}>Favorites & notifications</h1>
            <WalkthroughLegendRow icon="★" title="Favorites" body="Star a trader to always see their moves, bypassing every filter above." />
            <WalkthroughLegendRow icon="⚙" title="Push notifications" body="Get pinged the moment an alert fires." />
            <WalkthroughLegendRow icon="☾" title="Quiet hours" body="Mute notifications overnight — alerts still log to the feed." />
          </>
        )}

        <div style={{ display:"flex", alignItems:"center", gap:20, marginTop:26 }}>
          {step > 0 && <span onClick={back} style={{ fontSize:14, fontWeight:500, color:C.sage, cursor:"pointer" }}>Back</span>}
          <div style={{ flex:1 }}><PrimaryButton onClick={next} style={{ width:"100%" }}>{last ? "Start exploring" : "Next"}</PrimaryButton></div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SIDEBAR
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { key:"alerts",      label:"Alerts",         icon:"◎" },
  { key:"whale",       label:"Whale",          icon:"◆" },
  { key:"dormant",     label:"Dormant Wallet", icon:"☾" },
  { key:"leaderboard", label:"Leaderboard",    icon:"≡" },
  { key:"favorites",   label:"Favorites",      icon:"★" },
  { key:"settings",    label:"Settings",       icon:"⚙" },
];

function Sidebar({ activeTab, onTab, unread, wsStatus, email, onLogout }) {
  const dotColor = { live:C.green, connecting:C.amber, reconnecting:C.amber, offline:C.red }[wsStatus] ?? C.sage;
  const dotLabel = { live:"Live", connecting:"Connecting…", reconnecting:"Reconnecting…", offline:"Offline" }[wsStatus] ?? wsStatus;

  return (
    <div style={{ width:SIDEBAR_W, flexShrink:0, background:C.sidebar, borderRight:`1px solid ${C.panelEdge}`, display:"flex", flexDirection:"column", height:"100vh", position:"sticky", top:0, zIndex:1 }}>
      {/* Logo */}
      <div style={{ padding:"24px 20px 20px", borderBottom:`1px solid ${C.panelEdge}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:30, height:30, borderRadius:8, background:C.green, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <span style={{ width:10, height:10, border:`2px solid ${C.bg}`, borderRadius:3 }} />
          </div>
          <span style={{ fontSize:14, fontWeight:700, letterSpacing:-0.2, fontFamily:MONO, lineHeight:1.2 }}>Follow the<br/>Leader</span>
        </div>
      </div>

      {/* WS status — ambient live indicator */}
      <div style={{ padding:"12px 20px 10px", borderBottom:`1px solid ${C.panelEdge}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:dotColor, flexShrink:0, boxShadow:wsStatus==="live"?`0 0 6px ${dotColor}88`:"none", animation:wsStatus==="live"?"wsPulse 2.8s ease-in-out infinite":"none" }} />
          <span style={{ fontSize:11.5, color:C.sage }}>{dotLabel}</span>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:"12px 12px 0", overflowY:"auto" }}>
        {TABS.map(({ key, label, icon }) => {
          const on = activeTab === key;
          return (
            <button key={key} onClick={() => onTab(key)} style={{ width:"100%", display:"flex", alignItems:"center", gap:11, padding:"10px 12px", borderRadius:10, marginBottom:3, background:on?C.greenDim:"transparent", border:`1px solid ${on?C.green+"33":"transparent"}`, cursor:"pointer", color:on?C.green:C.sage, transition:"background 120ms, color 120ms", textAlign:"left" }}>
              <span style={{ fontSize:16, lineHeight:1 }}>{icon}</span>
              <span style={{ fontSize:14, fontWeight:on?600:400 }}>{label}</span>
              {key === "alerts" && unread > 0 && (
                <span style={{ marginLeft:"auto", minWidth:18, height:18, borderRadius:999, background:C.green, color:C.bg, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px" }}>{unread > 99 ? "99+" : unread}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User */}
      <div style={{ padding:"14px 16px 20px", borderTop:`1px solid ${C.panelEdge}` }}>
        <div style={{ fontSize:12, color:C.sage, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{email}</div>
        <button onClick={onLogout} style={{ background:"none", border:"none", color:C.sageDim, cursor:"pointer", fontSize:12, padding:0, marginTop:4 }}>Sign out</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ALERTS TAB
// ══════════════════════════════════════════════════════════════════════════════
const SETTINGS_DEFAULTS = {
  conviction:2.0, winFloor:0.6, favBypass:true, notif:true, quiet:false, from:"22:00", to:"08:00",
  whaleUsd: 0, dormancyDays: 5.0, excludeWhale: false, excludeDormant: false,
};

function AlertCard({ a, read, onRead, favSet, onSelectTrader, sample, rankWindow = "day" }) {
  const buy = a.side === "BUY";
  const fav = favSet ? favSet.has(a.trader) : false;
  const url = marketUrl(a.slug, a.eventSlug);
  const windowRank  = { day: a.rank, week: a.weekRank, month: a.monthRank, all: a.allRank }[rankWindow];
  const periodLabel = { day: "today", week: "this week", month: "this month", all: "all time" }[rankWindow];
  return (
    <div
      onClick={() => !read && !sample && onRead(a.txHash)}
      style={{ position:"relative", background:C.panel, border:`1px solid ${read||sample ? C.panelEdge : C.green+"33"}`, borderRadius:14, padding:"16px 18px", cursor:read||sample?"default":"pointer", opacity:read?0.68:1, transition:"opacity 160ms, border-color 160ms" }}
    >
      {sample && <span style={{ position:"absolute", top:-9, left:16, fontSize:10, fontWeight:600, letterSpacing:0.4, textTransform:"uppercase", color:C.sage, background:C.bg, padding:"1px 8px", borderRadius:6, border:`1px solid ${C.panelEdge}` }}>Sample</span>}
      {!read && !sample && <span style={{ position:"absolute", top:18, right:18, width:7, height:7, borderRadius:"50%", background:C.green, boxShadow:`0 0 7px ${C.green}aa` }} />}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12, paddingRight:20 }}>
        <TraderAvatar id={a.trader} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13.5, fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>
            <button onClick={(e) => { e.stopPropagation(); onSelectTrader?.(a); }} style={{ background:"none", border:"none", padding:0, cursor:"pointer", color:C.text, fontWeight:600, fontSize:13.5, fontFamily:FONT }}>
              {displayName(a.username, a.trader)}
            </button>
            {fav && <span style={{ color:C.amber, fontSize:11 }}>★</span>}
          </div>
          <div style={{ fontSize:11.5, color:C.sage }}>
            {windowRank != null && <>#{windowRank} {periodLabel} · </>}
            <WinRate winRate={a.winRate} resolved={a.resolved} />
          </div>
        </div>
        {a.conviction != null
          ? <Badge title="How many times this trader's typical position size." bg={C.amberDim} bd={`${C.amber}55`} fg={C.amber} bold>{a.conviction.toFixed(1)}× usual</Badge>
          : fav ? <Badge title="From a favorited trader." bg="transparent" bd={`${C.amber}55`} fg={C.amber}>★ favorite</Badge>
          : null}
      </div>
      <div style={{ fontSize:15, fontWeight:600, lineHeight:1.35, marginBottom:10 }}>
        <span style={{ color:buy?C.green:C.red }}>{buy?"Bought":"Sold"}</span>{" "}
        <OutcomePill o={a.outcome} />{" "}
        <span style={{ color:C.text }}> · {a.market}</span>
      </div>
      {(a.categories?.includes("whale") || a.dormantDays != null) && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
          {a.categories?.includes("whale") && (
            <Badge bg={C.amberDim} bd={`${C.amber}55`} fg={C.amber}>◆ whale</Badge>
          )}
          {a.categories?.includes("dormant_wallet") && a.dormantDays != null && (
            <Badge bg="transparent" bd={`${C.sage}55`} fg={C.sage}>
              ☾ inactive {Math.round(a.dormantDays)}d
            </Badge>
          )}
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", gap:12, fontSize:12, color:C.sage }}>
        <span style={{ fontWeight:700, color:C.text }}>{fmtUsd(a.usd, false)}</span>
        {a.price != null && <span>@ {a.price.toFixed(2)}</span>}
        {a.side === "BUY" && a.potentialPayout != null && (
          <span style={{ color:C.amber }}>
            → {fmtUsd(a.potentialPayout, false)} to win
            {a.impliedReturnPct != null && ` · +${a.impliedReturnPct.toFixed(0)}%`}
          </span>
        )}
        <span>· {timeAgo(a.ts)}</span>
        <span style={{ flex:1 }} />
        {url && <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color:C.sage, textDecoration:"none", fontSize:12 }}>market ↗</a>}
      </div>
    </div>
  );
}

function AlertsPane({ favSet, rankCtl, settings, accessToken, onSelectTrader, onUnreadChange, onWsStatusChange }) {
  const { topN, favBypassRank, rankWindow, setRankWindow } = rankCtl;
  const { conviction, winFloor, favBypass, whaleUsd, dormancyDays, excludeWhale, excludeDormant } = settings;
  const excludedCategories = [excludeWhale && "whale", excludeDormant && "dormant_wallet"].filter(Boolean).join(",");
  const [alerts, setAlerts]     = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [subTab, setSubTab]     = useState("all");
  const [readSet, setReadSet]   = useState(() => new Set());
  const seenHashes = useRef(new Set());

  const changeRankWindow = (w) => {
    setRankWindow(w);
    saveDeviceSettings({ rank_window: w });
  };

  useEffect(() => {
    setLoading(true);
    fetchAlerts({
      convictionMin:conviction, winRateMin:winFloor, topN, rankWindow, limit:75,
      whaleUsdThreshold: whaleUsd, dormancyThresholdDays: dormancyDays, excludedCategories,
    })
      .then(({ data, fromMock: fm }) => {
        setAlerts(data);
        setFromMock(fm);
        data.forEach((a) => seenHashes.current.add(a.txHash));
        setLoading(false);
      });
  }, [conviction, winFloor, topN, rankWindow, whaleUsd, dormancyDays, excludedCategories]);

  const { wsStatus } = useAuthenticatedAlertStream({
    url: accessToken
      ? `${WS_BASE}/ws?device_id=${encodeURIComponent(DEVICE_ID)}&token=${encodeURIComponent(accessToken)}`
      : null,
    onAlert: (a) => {
      if (seenHashes.current.has(a.txHash)) return;
      seenHashes.current.add(a.txHash);
      setAlerts((p) => [a, ...p]);
    },
    onAuthError: async () => {
      await tryRefreshToken();
    },
    enabled: true,
  });

  useEffect(() => { onWsStatusChange?.(wsStatus); }, [wsStatus, onWsStatusChange]);

  const markRead = (h) => setReadSet((p) => new Set(p).add(h));
  const visible = useMemo(
    () => filterVisibleAlerts(alerts, {
      favSet, topN, favBypassRank, conviction, winFloor, favBypass, rankWindow,
      whaleUsd, dormancyDays, excludeWhale, excludeDormant,
    }),
    [alerts, favSet, topN, favBypassRank, conviction, winFloor, favBypass, rankWindow,
     whaleUsd, dormancyDays, excludeWhale, excludeDormant]
  );

  const displayed = subTab === "favorites" ? visible.filter((a) => favSet.has(a.trader)) : visible;
  const unread    = visible.filter((a) => !readSet.has(a.txHash)).length;

  // Report unread count up to root for the sidebar badge
  useEffect(() => { onUnreadChange?.(unread); }, [unread, onUnreadChange]);

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      {/* Pane header */}
      <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", gap:14, flexShrink:0 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, letterSpacing:-0.5, display:"flex", alignItems:"center", gap:10 }}>
          Alerts
          {unread > 0 && <span style={{ fontSize:11, fontWeight:700, color:C.bg, background:C.green, borderRadius:999, padding:"2px 8px" }}>{unread}</span>}
        </h2>
        <div style={{ flex:1 }} />
        {unread > 0 && <button onClick={() => setReadSet(new Set(visible.map((a) => a.txHash)))} style={{ background:"transparent", border:"none", color:C.sage, fontSize:13, cursor:"pointer" }}>Mark all read</button>}
      </div>

      {/* Window filter — which leaderboard window's rank gates these alerts */}
      <div style={{ padding:"12px 24px 0", flexShrink:0 }}>
        <Pills value={rankWindow} onChange={changeRankWindow} options={[["day","Day"],["week","Week"],["month","Month"],["all","All time"]]} dimLast />
        {rankWindow === "all" && <div style={{ marginTop:10 }}><Note tone="amber">All-time ranking can surface stale signals. Day view is better for live activity.</Note></div>}
      </div>

      {/* Filter bar */}
      <div style={{ padding:"12px 24px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", gap:12, flexShrink:0, flexWrap:"wrap" }}>
        <Pills value={subTab} onChange={setSubTab} options={[["all","All"],["favorites","Favorites"]]} />
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:11, color:C.sageDim, letterSpacing:0.4, textTransform:"uppercase" }}>Rank</span>
          {RANK_CHIPS.map((n) => {
            const on = rankCtl.topN === n;
            return <button key={n} onClick={() => rankCtl.setTopN(n)} style={{ padding:"5px 10px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`1px solid ${on?C.green+"55":C.panelEdge}`, background:on?C.greenDim:"transparent", color:on?C.green:C.sage }}>{n===RANK_MAX?"All":n}</button>;
          })}
        </div>
      </div>

      {/* Feed */}
      <div style={{ flex:1, overflowY:"auto", padding:"16px 24px 24px" }}>
        {fromMock && <MockBanner />}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {loading ? <Spinner size={20} /> : displayed.length === 0
            ? <Empty>{subTab==="favorites" ? "No alerts from your favorites yet." : "No alerts matching your current filters. Try lowering conviction or widening the rank filter."}</Empty>
            : displayed.map((a) => <AlertCard key={a.txHash} a={a} read={readSet.has(a.txHash)} onRead={markRead} favSet={favSet} onSelectTrader={onSelectTrader} rankWindow={rankWindow} />)}
        </div>
        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:24, lineHeight:1.5 }}>
          Public trading activity from Polymarket, shown for information only. Not affiliated with or endorsed by Polymarket. Not financial advice.
        </p>
      </div>
    </div>
  );
}

// A dedicated, unfiltered feed of alerts tagged with one category (computed
// server-side from fixed system thresholds — see core.py's
// compute_alert_categories) — deliberately separate from AlertsPane's
// conviction/win-rate/rank-gated feed, and independent of a device's own
// whale/dormancy settings or excluded-categories toggle: always shows every
// alert with the tag, regardless of either.
function CategoryAlertsPane({ category, title, emptyText, favSet, accessToken, onSelectTrader }) {
  const [alerts, setAlerts]     = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [readSet, setReadSet]   = useState(() => new Set());
  const seenHashes = useRef(new Set());

  useEffect(() => {
    setLoading(true);
    fetchAlerts({ topN: RANK_MAX, limit: 75, category })
      .then(({ data, fromMock: fm }) => {
        setAlerts(data);
        setFromMock(fm);
        data.forEach((a) => seenHashes.current.add(a.txHash));
        setLoading(false);
      });
  }, [category]);

  // main.py's broadcast() sends every category-tagged trade to every
  // connected device regardless of that device's own regular filters,
  // specifically so this live stream works here.
  useAuthenticatedAlertStream({
    url: accessToken
      ? `${WS_BASE}/ws?device_id=${encodeURIComponent(DEVICE_ID)}&token=${encodeURIComponent(accessToken)}`
      : null,
    onAlert: (a) => {
      if (!a.categories?.includes(category)) return;
      if (seenHashes.current.has(a.txHash)) return;
      seenHashes.current.add(a.txHash);
      setAlerts((p) => [a, ...p]);
    },
    onAuthError: async () => {
      await tryRefreshToken();
    },
    enabled: true,
  });

  const markRead = (h) => setReadSet((p) => new Set(p).add(h));
  const visible = useMemo(() => {
    const seen = new Set();
    return alerts
      .filter((a) => {
        if (seen.has(a.txHash)) return false;
        seen.add(a.txHash);
        return a.categories?.includes(category);
      })
      .sort((x, y) => y.ts - x.ts);
  }, [alerts, category]);

  const unread = visible.filter((a) => !readSet.has(a.txHash)).length;

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", gap:14, flexShrink:0 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, letterSpacing:-0.5, display:"flex", alignItems:"center", gap:10 }}>
          {title}
          {unread > 0 && <span style={{ fontSize:11, fontWeight:700, color:C.bg, background:C.green, borderRadius:999, padding:"2px 8px" }}>{unread}</span>}
        </h2>
        <div style={{ flex:1 }} />
        {unread > 0 && <button onClick={() => setReadSet(new Set(visible.map((a) => a.txHash)))} style={{ background:"transparent", border:"none", color:C.sage, fontSize:13, cursor:"pointer" }}>Mark all read</button>}
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"16px 24px 24px" }}>
        {fromMock && <MockBanner />}
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {loading ? <Spinner size={20} /> : visible.length === 0
            ? <Empty>{emptyText}</Empty>
            : visible.map((a) => <AlertCard key={a.txHash} a={a} read={readSet.has(a.txHash)} onRead={markRead} favSet={favSet} onSelectTrader={onSelectTrader} />)}
        </div>
        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:24, lineHeight:1.5 }}>
          Public trading activity from Polymarket, shown for information only. Not affiliated with or endorsed by Polymarket. Not financial advice.
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LEADERBOARD TAB
// ══════════════════════════════════════════════════════════════════════════════
function LeaderboardPane({ favSet, toggleFav, onSelectTrader, rankCtl }) {
  const { topN, favBypassRank } = rankCtl;
  const [win, setWin]         = useState("day");
  const [traders, setTraders] = useState([]);
  const [fromMock, setFromMock] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLeaderboard(win).then(({ data, fromMock: fm }) => { setTraders(data); setFromMock(fm); setLoading(false); });
  }, [win]);

  const favs     = traders.filter((t) => favSet.has(t.id));
  const rest     = traders.filter((t) => !favSet.has(t.id) && t.rank <= topN);
  const hidCount = traders.filter((t) => !favSet.has(t.id) && t.rank > topN).length;
  const periodLabel = win === "day" ? "today" : win === "week" ? "this week" : win === "month" ? "this month" : "all time";

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.panelEdge}`, flexShrink:0 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, letterSpacing:-0.5 }}>Leaderboard</h2>
      </div>
      <div style={{ padding:"12px 24px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", gap:12, flexShrink:0, flexWrap:"wrap" }}>
        <Pills value={win} onChange={setWin} options={[["day","Day"],["week","Week"],["month","Month"],["all","All time"]]} dimLast />
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:11, color:C.sageDim, letterSpacing:0.4, textTransform:"uppercase" }}>Rank</span>
          {RANK_CHIPS.map((n) => {
            const on = rankCtl.topN === n;
            return <button key={n} onClick={() => rankCtl.setTopN(n)} style={{ padding:"5px 10px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", border:`1px solid ${on?C.green+"55":C.panelEdge}`, background:on?C.greenDim:"transparent", color:on?C.green:C.sage }}>{n===RANK_MAX?"All":n}</button>;
          })}
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"16px 24px 24px" }}>
        {fromMock && <MockBanner />}
        {win === "all" && <div style={{ marginBottom:14 }}><Note tone="amber">All-time ranking can surface stale signals. Day view is better for live activity.</Note></div>}
        {loading ? <Spinner size={20} /> : (
          <>
            {favs.length > 0 && (
              <div style={{ marginBottom:22 }}>
                <SectionLabel color={C.amber}>★ Your favorites</SectionLabel>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {favs.map((t) => <TraderRowDesktop key={t.id} t={t} fav onFav={toggleFav} onOpen={onSelectTrader} periodLabel={periodLabel} />)}
                </div>
              </div>
            )}
            <div>
              {favs.length > 0 && <SectionLabel>Top {topN < RANK_MAX ? `top ${topN}` : "200"}</SectionLabel>}
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {rest.map((t) => <TraderRowDesktop key={t.id} t={t} fav={false} onFav={toggleFav} onOpen={onSelectTrader} periodLabel={periodLabel} />)}
              </div>
              {hidCount > 0 && <p style={{ fontSize:12, color:C.sage, textAlign:"center", marginTop:14, opacity:0.75 }}>{hidCount} more outside the top {topN}. Widen the rank filter to see them.</p>}
            </div>
          </>
        )}
        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:24, lineHeight:1.5 }}>
          Data from Polymarket's public leaderboard. Not affiliated with or endorsed by Polymarket.
        </p>
      </div>
    </div>
  );
}

function TraderRowDesktop({ t, fav, onFav, onOpen, periodLabel = "today" }) {
  return (
    <div onClick={() => onOpen(t)} role="button" tabIndex={0}
         style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:C.panel, border:`1px solid ${C.panelEdge}`, borderRadius:12, cursor:"pointer" }}
         onMouseEnter={(e) => e.currentTarget.style.background = C.panelHov}
         onMouseLeave={(e) => e.currentTarget.style.background = C.panel}>
      <div style={{ width:28, textAlign:"center", fontSize:14, fontWeight:700, color:t.rank<=3?C.green:C.sage, flexShrink:0, fontFamily:MONO }}>#{t.rank}</div>
      <TraderAvatar id={t.id} size={30} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:14, fontWeight:600, marginBottom:2 }}>{displayName(t.username, t.id)}</div>
        <div style={{ fontSize:12 }}><WinRate winRate={t.winRate} resolved={t.resolved} /></div>
      </div>
      <div style={{ textAlign:"right", flexShrink:0 }}>
        {/* PnL for whichever window this leaderboard is currently sorted by
            — was hardcoded to pnlDay/"today" regardless of the selected
            pill, which is exactly the kind of mismatch that makes a
            trader's PnL look wildly wrong relative to everything else
            shown about them. */}
        <div style={{ fontSize:14, fontWeight:700, color:t.pnlPeriod>=0?C.green:C.red }}>{fmtUsd(t.pnlPeriod)}</div>
        <div style={{ fontSize:11, color:C.sage, marginTop:2 }}>{periodLabel}</div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onFav(t.id); }}
              title={fav?"Favorited":"Favorite to bypass filters."}
              style={{ background:"transparent", border:"none", cursor:"pointer", padding:6, color:fav?C.amber:C.sageDim, fontSize:17 }}>
        {fav ? "★" : "☆"}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FAVORITES TAB
// ══════════════════════════════════════════════════════════════════════════════
function FavoritesPane({ favSet, toggleFav, onSelectTrader, traders }) {
  const favs = traders.filter((t) => favSet.has(t.id));
  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.panelEdge}`, flexShrink:0 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, letterSpacing:-0.5 }}>Favorites</h2>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:"16px 24px 24px" }}>
        {favs.length === 0
          ? <Empty>No favorites yet. Star a trader on the leaderboard to follow every move they make — even ones that don't clear your filters.</Empty>
          : (
            <>
              <Note tone="amber">Alerts from these traders bypass your conviction and win-rate filters.</Note>
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:14 }}>
                {favs.map((t) => <TraderRowDesktop key={t.id} t={t} fav onFav={toggleFav} onOpen={onSelectTrader} />)}
              </div>
            </>
          )}
        <p style={{ fontSize:11.5, color:C.sageDim, textAlign:"center", marginTop:24, lineHeight:1.5 }}>
          Not affiliated with or endorsed by Polymarket.
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS TAB
// ══════════════════════════════════════════════════════════════════════════════
function SettingsPane({ favSet, toggleFav, rankCtl, settings, setSettings, email, traders, subStatus, refreshSubStatus, onReplayWalkthrough }) {
  const usernameById = useMemo(() => new Map(traders.map((t) => [t.id, t.username])), [traders]);
  const { topN, setTopN, favBypassRank, setFavBypassRank, setRankWindow } = rankCtl;
  const [savedAt, setSavedAt]         = useState(null);
  const [backendSaved, setBackendSaved] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [resumeLoading, setResumeLoading]     = useState(false);
  const [resumeError, setResumeError]         = useState("");
  const saveTimer   = useRef(null);
  const debounceRef = useRef(null);

  const persist = useCallback((patch) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const nextExcludeWhale   = patch.excludeWhale   ?? settings.excludeWhale;
      const nextExcludeDormant = patch.excludeDormant ?? settings.excludeDormant;
      const { error } = await saveDeviceSettings({
        conviction_multiplier: patch.conviction ?? settings.conviction,
        win_rate_floor:        patch.winFloor   ?? settings.winFloor,
        top_n:                 patch.top_n            ?? topN,
        fav_bypass_rank:       patch.fav_bypass_rank  ?? favBypassRank,
        fav_bypass_filters:    patch.favBypass  ?? settings.favBypass,
        notif_enabled:         patch.notif      ?? settings.notif,
        quiet_from:            (patch.quiet ?? settings.quiet) ? (patch.from ?? settings.from) : null,
        quiet_to:              (patch.quiet ?? settings.quiet) ? (patch.to   ?? settings.to)   : null,
        whale_usd_threshold:     patch.whaleUsd     ?? settings.whaleUsd,
        dormancy_threshold_days: patch.dormancyDays ?? settings.dormancyDays,
        excluded_categories:     [nextExcludeWhale && "whale", nextExcludeDormant && "dormant_wallet"].filter(Boolean).join(","),
      });
      setBackendSaved(!error);
    }, 800);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSavedAt(Date.now()), 200);
  }, [settings, topN, favBypassRank]);

  const up      = (patch) => { setSettings((p) => ({ ...p, ...patch })); persist(patch); };
  const setRank = (n)     => { setTopN(n); persist({ top_n: n }); };
  const saved   = savedAt && Date.now() - savedAt < 2500;

  const openPortal = async () => {
    setPortalLoading(true); setPortalError("");
    const { url, error } = await openBillingPortal();
    setPortalLoading(false);
    if (error || !url) { setPortalError(error ?? "Portal unavailable."); return; }
    window.open(url, "_blank", "noopener noreferrer");
  };

  const handleCancel = async () => {
    const { data, error } = await cancelSubscription();
    if (error || !data) throw new Error(error ?? "Cancel failed");
    setCancelModalOpen(false);
    await refreshSubStatus();
  };

  const handleResume = async () => {
    setResumeLoading(true); setResumeError("");
    const { data, error } = await resumeSubscription();
    setResumeLoading(false);
    if (error || !data) { setResumeError(error ?? "Resume failed"); return; }
    await refreshSubStatus();
  };

  return (
    <div style={{ height:"100%", display:"flex", flexDirection:"column" }}>
      <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:700, letterSpacing:-0.5 }}>Settings</h2>
        <span style={{ fontSize:12.5, color:C.green, opacity:saved?1:0, transition:"opacity 200ms" }}>
          {backendSaved ? "✓ Saved" : "✓ Saved locally"}
        </span>
      </div>

      <div style={{ flex:1, overflowY:"auto" }}>
        <div style={{ maxWidth:620, padding:"20px 24px 40px", display:"flex", flexDirection:"column", gap:20 }}>

          {/* Help */}
          <section>
            <SectionLabel>Help</SectionLabel>
            <Card>
              <div onClick={onReplayWalkthrough} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:500 }}>How alerts work</div>
                  <div style={{ fontSize:12.5, color:C.sage, marginTop:3 }}>Replay the walkthrough</div>
                </div>
                <span style={{ fontSize:16, color:C.sage }}>›</span>
              </div>
            </Card>
          </section>

          {/* Subscription */}
          <section>
            <SectionLabel>Subscription</SectionLabel>
            <Card>
              {subStatus && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:15, fontWeight:700 }}>
                    {PLANS.find((p) => p.key === subStatus.plan)?.label ?? subStatus.plan ?? "No active plan"}
                  </div>
                  <div style={{ fontSize:13, color:C.sage, marginTop:3 }}>
                    {subStatus.subscription_status === "trialing"
                      ? `Trial ends ${fmtDate(subStatus.trial_end)}`
                      : subStatus.cancel_at_period_end
                      ? `Cancels ${fmtDate(subStatus.current_period_end)}`
                      : subStatus.subscription_status === "active" || subStatus.subscription_status === "past_due"
                      ? `Renews ${fmtDate(subStatus.current_period_end)}`
                      : "No active subscription"}
                  </div>
                </div>
              )}

              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontSize:15, fontWeight:600 }}>Manage billing</div>
                  <div style={{ fontSize:13, color:C.sage, marginTop:3 }}>Update payment method or view invoices.</div>
                </div>
                <PrimaryButton onClick={openPortal} loading={portalLoading} style={{ padding:"9px 18px", fontSize:13.5, flexShrink:0 }}>
                  Billing portal ↗
                </PrimaryButton>
              </div>
              {portalError && <div style={{ fontSize:12.5, color:C.red, marginTop:10 }}>{portalError}</div>}

              {subStatus?.cancel_at_period_end ? (
                <>
                  <Divider />
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:600 }}>Resume subscription</div>
                      <div style={{ fontSize:13, color:C.sage, marginTop:3 }}>Undo the pending cancellation.</div>
                    </div>
                    <PrimaryButton onClick={handleResume} loading={resumeLoading} style={{ padding:"9px 18px", fontSize:13.5, flexShrink:0 }}>
                      Resume
                    </PrimaryButton>
                  </div>
                  {resumeError && <div style={{ fontSize:12.5, color:C.red, marginTop:10 }}>{resumeError}</div>}
                </>
              ) : subStatus && subStatus.subscription_status !== "none" && subStatus.subscription_status !== "canceled" ? (
                <>
                  <Divider />
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:600 }}>Cancel subscription</div>
                      <div style={{ fontSize:13, color:C.sage, marginTop:3 }}>Keep access until the end of the billing period.</div>
                    </div>
                    <button
                      onClick={() => setCancelModalOpen(true)}
                      style={{ background:"transparent", border:`1px solid ${C.red}44`, color:C.red, borderRadius:10, padding:"9px 16px", fontSize:13.5, fontWeight:600, cursor:"pointer", flexShrink:0 }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : null}
            </Card>
          </section>

          {cancelModalOpen && (
            <CancelSubscriptionModal
              periodEndLabel={fmtDate(subStatus?.current_period_end)}
              onConfirm={handleCancel}
              onDismiss={() => setCancelModalOpen(false)}
            />
          )}

          {/* Rank filter */}
          <section>
            <SectionLabel>Leaderboard rank</SectionLabel>
            <Card>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
                <span style={{ fontSize:15, fontWeight:500 }}>Show top traders</span>
                <span style={{ fontSize:15, fontWeight:700, color:C.green }}>{topN >= RANK_MAX ? "All (200)" : `Top ${topN}`}</span>
              </div>
              <div style={{ fontSize:12.5, color:C.sage, marginBottom:12, lineHeight:1.4 }}>Only alert on trades from traders ranked within this many on today's leaderboard.</div>
              <input type="range" min={1} max={RANK_MAX} step={1} value={topN} onChange={(e) => setRank(parseInt(e.target.value,10))} style={{ width:"100%", accentColor:C.green }} />
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.sage, marginTop:2 }}><span>Top 1 (tightest)</span><span>All 200</span></div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:12 }}>
                {RANK_CHIPS.map((n) => {
                  const on = topN === n;
                  return <button key={n} onClick={() => setRank(n)} style={{ padding:"5px 12px", borderRadius:8, fontSize:12.5, fontWeight:600, cursor:"pointer", border:`1px solid ${on?C.green+"55":C.panelEdge}`, background:on?C.greenDim:"transparent", color:on?C.green:C.sage }}>{n===RANK_MAX?"All":n}</button>;
                })}
              </div>
              <Divider />
              <SettingsSwitch label="Favorites bypass rank filter" sub="Favorited traders alert you even when they fall outside the top N today." on={favBypassRank} onChange={(v) => { setFavBypassRank(v); persist({ fav_bypass_rank: v }); }} />
            </Card>
          </section>

          {/* Alert sensitivity */}
          <section>
            <SectionLabel>Alert sensitivity</SectionLabel>
            <Card>
              <SettingsSlider label="Conviction multiplier" value={`${settings.conviction.toFixed(1)}×`} color={C.amber}
                desc="Alert only when a trade is at least this many times the trader's own usual position size."
                min={1} max={5} step={0.1} v={settings.conviction} onChange={(v) => up({ conviction:v })} lo="1.0× (noisy)" hi="5.0× (huge only)" />
              <Divider />
              <SettingsSlider label="Minimum win rate" value={`${Math.round(settings.winFloor*100)}%`} color={C.green}
                desc="Based on the trader's recently resolved markets. Traders without much history yet are flagged 'small sample', not hidden."
                min={0} max={0.9} step={0.05} v={settings.winFloor} onChange={(v) => up({ winFloor:v })} lo="0% (all traders)" hi="90%" />
              <Divider />
              <SettingsSwitch label="Whale alert (absolute $)" sub="Also alert on any trade over this dollar amount, regardless of conviction."
                on={settings.whaleUsd > 0} onChange={(v) => up({ whaleUsd: v ? 25000 : 0 })} />
              {settings.whaleUsd > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:12 }}>
                  <span style={{ fontSize:15, fontWeight:600, color:C.text }}>$</span>
                  <input type="number" min={0} step={1000} value={settings.whaleUsd}
                         onChange={(e) => up({ whaleUsd: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                         style={{ flex:1, fontSize:15, fontWeight:600, padding:"6px 10px", borderRadius:8, border:`1px solid ${C.panelEdge}`, background:"transparent", color:C.text }} />
                </div>
              )}
              <Divider />
              <SettingsSlider label="Dormant-wallet alert" value={`${settings.dormancyDays.toFixed(0)}d`} color={C.sage}
                desc="Also alert when a trader who's been quiet this many days suddenly trades again."
                min={1} max={90} step={1} v={settings.dormancyDays} onChange={(v) => up({ dormancyDays: Math.round(v) })} lo="1 day" hi="90 days" />
            </Card>
          </section>

          {/* Alert categories */}
          <section>
            <SectionLabel>Alert categories</SectionLabel>
            <Card>
              <SettingsSwitch label="Hide whale-tagged alerts" sub={`Alerts over $${WHALE_CATEGORY_USD_FLOOR.toLocaleString()} are tagged "whale" — hide them even if otherwise alert-worthy.`}
                on={settings.excludeWhale} onChange={(v) => up({ excludeWhale:v })} />
              <Divider />
              <SettingsSwitch label="Hide dormant-wallet alerts" sub={`Trades from wallets inactive ${DORMANCY_CATEGORY_DAYS.toFixed(0)}+ days are tagged "dormant_wallet" — hide them.`}
                on={settings.excludeDormant} onChange={(v) => up({ excludeDormant:v })} />
            </Card>
          </section>

          {/* Favorites */}
          <section>
            <SectionLabel>Favorites</SectionLabel>
            <Card>
              <SettingsSwitch label="Favorites bypass filters" sub="Trust these traders enough to see every move, regardless of conviction or win rate." on={settings.favBypass} onChange={(v) => up({ favBypass:v })} />
              {favSet.size > 0 && (
                <>
                  <Divider />
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {[...favSet].map((id) => (
                      <div key={id} style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ color:C.amber, fontSize:15 }}>★</span>
                        <div style={{ flex:1, minWidth:0, fontSize:14, fontWeight:600, fontFamily:MONO }}>{displayName(usernameById.get(id), id)}</div>
                        <button onClick={() => toggleFav(id)} style={{ background:"transparent", border:`1px solid ${C.panelEdge}`, color:C.sage, fontSize:12, padding:"5px 12px", borderRadius:8, cursor:"pointer" }}>Remove</button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </section>

          {/* Quiet hours */}
          <section>
            <SectionLabel>Notifications</SectionLabel>
            <Card>
              <SettingsSwitch label="Quiet hours" sub="Mute notifications during a window. Alerts still log to the feed." on={settings.quiet} onChange={(v) => up({ quiet:v })} />
              {settings.quiet && (
                <div style={{ display:"flex", gap:16, marginTop:16 }}>
                  <TimeInput label="From" value={settings.from} onChange={(v) => up({ from:v })} />
                  <TimeInput label="To"   value={settings.to}   onChange={(v) => up({ to:v  })} />
                </div>
              )}
            </Card>
          </section>

          <button onClick={() => { setSettings(SETTINGS_DEFAULTS); setTopN(RANK_MAX); setFavBypassRank(true); setRankWindow("day"); persist({ ...SETTINGS_DEFAULTS, top_n: RANK_MAX, fav_bypass_rank: true, rank_window: "day" }); }}
                  style={{ padding:"11px 20px", borderRadius:10, border:`1px solid ${C.panelEdge}`, background:"transparent", color:C.sage, fontSize:14, cursor:"pointer" }}>
            Reset to defaults
          </button>

          <p style={{ fontSize:11.5, color:C.sageDim, lineHeight:1.5, marginTop:0 }}>
            Signed in as <strong style={{ color:C.textDim }}>{email}</strong> · Not affiliated with or endorsed by Polymarket. Not financial advice.
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsSwitch({ label, sub, on, onChange }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, justifyContent:"space-between" }}>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:15, fontWeight:500 }}>{label}</div>
        {sub && <div style={{ fontSize:12.5, color:C.sage, marginTop:3, lineHeight:1.4 }}>{sub}</div>}
      </div>
      <span onClick={() => onChange(!on)} role="switch" aria-checked={on} tabIndex={0}
            style={{ width:44, height:26, borderRadius:999, background:on?C.green:C.panelEdge2, position:"relative", flexShrink:0, cursor:"pointer", transition:"background 140ms" }}>
        <span style={{ position:"absolute", top:3, left:on?20:3, width:20, height:20, borderRadius:"50%", background:on?C.bg:C.sage, transition:"left 140ms" }} />
      </span>
    </div>
  );
}

function SettingsSlider({ label, value, color, desc, min, max, step, v, onChange, lo, hi }) {
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
        <span style={{ fontSize:15, fontWeight:500 }}>{label}</span>
        <span style={{ fontSize:15, fontWeight:700, color }}>{value}</span>
      </div>
      <div style={{ fontSize:12.5, color:C.sage, marginBottom:12, lineHeight:1.4 }}>{desc}</div>
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => onChange(parseFloat(e.target.value))} style={{ width:"100%", accentColor:C.green }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:C.sage, marginTop:2 }}><span>{lo}</span><span>{hi}</span></div>
    </div>
  );
}

function TimeInput({ label, value, onChange }) {
  return (
    <div style={{ flex:1 }}>
      <div style={{ fontSize:12, color:C.sage, marginBottom:5 }}>{label}</div>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width:"100%", background:C.bg, border:`1px solid ${C.panelEdge2}`, borderRadius:9, color:C.text, fontSize:14, padding:"9px 12px", colorScheme:"dark", boxSizing:"border-box" }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TRADER DETAIL PANEL (right-side panel, doesn't replace main view)
// ══════════════════════════════════════════════════════════════════════════════
function TraderDetailPanel({ trader, favSet, toggleFav, onClose, settings }) {
  const t = trader;
  const [open,    setOpen]   = useState([]);
  const [closed,  setClosed] = useState([]);
  // Real win_rate/resolved from closed-positions — NOT t.winRate/t.resolved,
  // which come from the leaderboard row and are always null/0.
  const [winRate,  setWinRate]  = useState(null);
  const [resolved, setResolved] = useState(0);
  const [value,   setValue]  = useState(null);
  const [valueHistory, setValueHistory] = useState([]);
  const [trades,  setTrades] = useState([]);
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesHasMore, setTradesHasMore] = useState(false);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exp,     setExp]    = useState(null);
  const [fromMock, setFromMock] = useState(false);
  const [timeline,        setTimeline]        = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const timelineRequestId = useRef(0);
  // Guards against a slower in-flight request for a previously-selected
  // trader resolving after a faster one for the trader now showing (this
  // panel stays mounted across row clicks, unlike mobile's per-trader
  // screen) and overwriting the current trader's data with a stale one's.
  const detailRequestId = useRef(0);
  const tradesRequestId = useRef(0);

  const fav   = favSet.has(t.id ?? t.trader);
  const id    = t.id ?? t.trader;
  const rank  = t.rank;
  const pct   = winRate != null ? Math.round(winRate * 100) : null;
  const small = resolved < 10;
  const TRADES_PAGE = 20;

  // Lazy-loads the buy/sell fill timeline the first time a closed position
  // is expanded, so a big realizedPnl with an empty "Open positions" list
  // is explainable instead of looking like a data error.
  //
  // rowKey identifies the card (unique even when /closed-positions returns
  // multiple rows for the same market — e.g. separate YES/NO outcome entries
  // — which share a conditionId); conditionId is the real market id used
  // for the timeline fetch, which is not outcome-scoped.
  const toggleExpanded = async (rowKey, conditionId) => {
    if (exp === rowKey) { setExp(null); return; }
    setExp(rowKey);
    setTimeline([]);
    setTimelineLoading(true);
    const requestId = ++timelineRequestId.current;
    try {
      const tl = await fetchPositionTimeline(id, conditionId);
      // A newer expand/collapse may have fired while this was in flight —
      // discard a late response so it can't overwrite a different
      // position's timeline.
      if (requestId !== timelineRequestId.current) return;
      setTimeline(tl.data);
    } finally {
      if (requestId === timelineRequestId.current) setTimelineLoading(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    setExp(null);
    const requestId = ++detailRequestId.current;
    Promise.all([
      fetchOpenPositions(id),
      fetchClosedPositions(id),
      fetchWalletValue(id),
      fetchValueHistory(id),
    ]).then(([o, c, v, vh]) => {
      if (requestId !== detailRequestId.current) return;
      setOpen(o.data);
      setClosed(c.data);
      setWinRate(c.winRate);
      setResolved(c.resolved);
      setValue(v.data);
      setValueHistory(vh.data);
      setFromMock(o.fromMock || c.fromMock);
      setLoading(false);
    });
  }, [id]);

  // Separate effect (not folded into the one above): trade history depends
  // on settings.conviction too, since the list is filtered server-side to
  // the user's conviction threshold — a slider change while this panel is
  // open should re-fetch the trade list without re-fetching everything else.
  useEffect(() => {
    const requestId = ++tradesRequestId.current;
    fetchTradeHistory(id, { limit: TRADES_PAGE, offset: 0, convictionMin: settings.conviction }).then((tr) => {
      if (requestId !== tradesRequestId.current) return;
      setTrades(tr.data);
      // Offset advances by the raw page size, not tr.data.length — the
      // conviction filter runs server-side on a fixed-size raw window, so a
      // filtered page is often shorter than TRADES_PAGE. Advancing by the
      // filtered count would re-request (and re-filter) trades we've seen.
      setTradesOffset(TRADES_PAGE);
      setTradesHasMore(tr.hasMore);
    });
  }, [id, settings.conviction]);

  const loadMoreTrades = () => {
    setTradesLoading(true);
    const requestId = ++tradesRequestId.current;
    fetchTradeHistory(id, { limit: TRADES_PAGE, offset: tradesOffset, convictionMin: settings.conviction }).then((tr) => {
      if (requestId !== tradesRequestId.current) return;
      setTrades((prev) => [...prev, ...tr.data]);
      setTradesOffset((prev) => prev + TRADES_PAGE);
      setTradesHasMore(tr.hasMore);
      setTradesLoading(false);
    });
  };

  return (
    <div style={{ width:DETAIL_W, flexShrink:0, borderLeft:`1px solid ${C.panelEdge}`, height:"100vh", position:"sticky", top:0, display:"flex", flexDirection:"column", background:C.panel }}>
      {/* Header */}
      <div style={{ padding:"16px 18px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", alignItems:"flex-start", gap:10, flexShrink:0 }}>
        <TraderAvatar id={id} size={36} />
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
            <span style={{ fontSize:15, fontWeight:700, fontFamily:MONO }}>{displayName(t.username, id)}</span>
            {fav && <span style={{ color:C.amber, fontSize:13 }}>★</span>}
          </div>
          {rank && <div style={{ fontSize:12, color:C.sage }}>#{rank} today</div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
          <button onClick={() => toggleFav(id)} title={fav ? "Remove from favorites" : "Add to favorites"}
                  style={{ background:"transparent", border:`1px solid ${C.panelEdge2}`, color:fav?C.amber:C.sage, fontSize:13.5, padding:"5px 10px", borderRadius:8, cursor:"pointer" }}>
            {fav ? "★ Favorited" : "☆ Favorite"}
          </button>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:C.sage, fontSize:20, cursor:"pointer", padding:"4px 6px", lineHeight:1 }}>✕</button>
        </div>
      </div>

      {/* Full wallet address — visible + copyable, so users can paste it
          into polymarket.com themselves rather than being stuck with the
          shortened display everywhere else. */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 18px 0", flexShrink:0 }}>
        <span style={{ fontSize:11.5, color:C.sage, fontFamily:MONO, userSelect:"all", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {id}
        </span>
        <button onClick={copyAddress}
                style={{ background:"transparent", border:"none", color:C.green, fontSize:11.5, fontWeight:600, cursor:"pointer", flexShrink:0, padding:0 }}>
          {copied ? "Copied ✓" : "Copy ⧉"}
        </button>
      </div>

      {/* Value chart */}
      {valueHistory.length >= 2 && (
        <div style={{ padding:"14px 18px 0" }}>
          <ValueChart points={valueHistory} width={DETAIL_W - 36} height={56} stroke={C.green} fill={`${C.green}22`} />
        </div>
      )}

      {/* Stats */}
      <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.panelEdge}`, display:"flex", gap:12, flexShrink:0, flexWrap:"wrap" }}>
        {/* "Open trades" — value of currently-open positions only, a
            point-in-time snapshot. Cumulative PnL for a period can (and
            often does) far exceed this, since it includes gains/losses
            already realized and no longer "open" — see Volume. */}
        <Stat label="Open trades" value={value != null ? fmtUsd(value, false) : "—"} />
        <Stat label="Win rate"  value={pct != null ? `${pct}%` : "—"} color={pct != null && pct >= 60 ? C.green : C.text} />
        <Stat label="PnL today" value={fmtUsd(t.pnlDay)} color={C.green} />
        <Stat label="PnL all"   value={fmtUsd(t.pnlAll)} color={C.green} />
        {/* Volume — for whichever leaderboard window (day/week/month/all)
            got you to this trader, i.e. the same window as t.pnlPeriod. */}
        <Stat label="Volume" value={fmtUsd(t.volPeriod, false)} />
        <Stat label="Resolved"  value={resolved} />
      </div>
      {fromMock && <div style={{ padding:"0 18px" }}><MockBanner /></div>}
      {small && !loading && (
        <div style={{ padding:"0 18px 10px" }}>
          <Note tone="amber">Small sample — only {resolved} resolved markets. Win rate may not be representative yet.</Note>
        </div>
      )}

      {/* Positions */}
      <div style={{ flex:1, overflowY:"auto", padding:"0 18px 24px" }}>
        {loading ? <Spinner size={20} /> : (
          <>
            <div style={{ height:16 }} />
            <SectionLabel>Open positions</SectionLabel>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:22 }}>
              {open.length === 0
                ? <Empty>No open positions found.</Empty>
                // Same conditionId can appear twice (separate YES/NO outcome
                // rows for one market) — see closed positions below.
                : open.map((p, idx) => (
                  <div key={`${p.conditionId ?? p.market}::${p.outcome}::${idx}`} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:C.bg, border:`1px solid ${C.panelEdge}`, borderRadius:12 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13.5, fontWeight:600, marginBottom:5, lineHeight:1.3 }}>
                        <MarketLink slug={p.slug} eventSlug={p.eventSlug} color={C.text}>{p.market}</MarketLink>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:12, color:C.sage }}>
                        <OutcomePill o={p.outcome} /><span>@ {p.price?.toFixed(2) ?? "—"}</span>
                      </div>
                    </div>
                    {/* currentValue = worth now; positionSizeUsd = real USD
                        entry cost — both matter and can differ a lot. */}
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontSize:14, fontWeight:700 }}>{fmtUsd(p.currentValue, false)}</div>
                      <div style={{ fontSize:10.5, color:C.sage, marginTop:2 }}>Entry {fmtUsd(p.positionSizeUsd, false)}</div>
                    </div>
                  </div>
                ))}
            </div>

            <SectionLabel>Resolved history</SectionLabel>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {closed.length === 0
                ? <Empty>No resolved positions found.</Empty>
                : closed.map((p, idx) => {
                  // /closed-positions can return multiple rows sharing one
                  // conditionId (e.g. separate YES/NO outcome entries for
                  // the same market) — conditionId alone isn't a unique key.
                  const k = `${p.conditionId ?? p.market}::${p.outcome}::${idx}`;
                  const isExp = exp === k;
                  return (
                    <div key={k} style={{ background:C.bg, border:`1px solid ${C.panelEdge}`, borderRadius:12, overflow:"hidden" }}>
                      <div onClick={() => toggleExpanded(k, p.conditionId)} role="button" tabIndex={0}
                           style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", cursor:"pointer" }}>
                        <div style={{ width:7, height:7, borderRadius:"50%", background:p.won?C.green:C.red, flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0, fontSize:13.5, fontWeight:600, lineHeight:1.3, whiteSpace:isExp?"normal":"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.market}</div>
                        <div style={{ fontSize:13.5, fontWeight:700, color:p.won?C.green:C.red, flexShrink:0 }}>{fmtUsd(p.pnl)}</div>
                        <span style={{ color:C.sage, fontSize:11, transform:isExp?"rotate(180deg)":"none", transition:"transform 120ms" }}>▾</span>
                      </div>
                      {isExp && (
                        <div style={{ padding:"0 14px 14px 31px", display:"flex", flexDirection:"column", gap:8, fontSize:12, color:C.sage }}>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                            <span style={{ display:"inline-flex", gap:5, alignItems:"center" }}>Position <OutcomePill o={p.outcome} /></span>
                            {/* Real USD cost basis — backend-computed from
                                totalBought(shares) × avgPrice (totalBought
                                is a share count despite the name). */}
                            <span>Size {fmtUsd(p.positionSizeUsd, false)}</span>
                            <span style={{ color:p.won?C.green:C.red, fontWeight:600 }}>{p.won?"Won":"Lost"}</span>
                            <span>{p.at ?? "—"}</span>
                            <MarketLink slug={p.slug} eventSlug={p.eventSlug}>View market</MarketLink>
                          </div>
                          {/* Buy/sell timeline — explains a big realizedPnl
                              with no matching "open position". */}
                          <div>
                            <div style={{ fontWeight:600, marginBottom:2, color:C.text }}>Timeline</div>
                            {timelineLoading ? (
                              <div>Loading…</div>
                            ) : timeline.length === 0 ? (
                              <div>No fill history found.</div>
                            ) : (
                              <>
                                <div>Entered {timeAgo(timeline[0].ts)} ({fmtUsd(timeline[0].usd, false)} first fill)</div>
                                {timeline.some((tl) => tl.side === "SELL") ? (
                                  <div>Sold before resolution — an early exit, not a hold-to-resolution win.</div>
                                ) : (
                                  <div>Held to resolution {p.resolvedTs ? timeAgo(p.resolvedTs) : "—"}.</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            <div style={{ height:22 }} />
            <SectionLabel>Trade history</SectionLabel>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {trades.length === 0
                ? <Empty>No trade history found.</Empty>
                : trades.map((tr) => (
                  <div key={tr.txHash} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:C.bg, border:`1px solid ${C.panelEdge}`, borderRadius:12 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13.5, fontWeight:600, marginBottom:5, lineHeight:1.3 }}>
                        <span style={{ color:tr.side==="BUY"?C.green:C.red }}>{tr.side==="BUY"?"Bought":"Sold"}</span>{" "}
                        <MarketLink slug={tr.slug} eventSlug={tr.eventSlug} color={C.text}>{tr.market}</MarketLink>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:12, color:C.sage }}>
                        <OutcomePill o={tr.outcome} /><span>@ {tr.price?.toFixed(2) ?? "—"} · {timeAgo(tr.ts)}</span>
                      </div>
                    </div>
                    <div style={{ fontSize:14, fontWeight:700, flexShrink:0 }}>{fmtUsd(tr.usd, false)}</div>
                  </div>
                ))}
              {tradesHasMore && (
                <button onClick={loadMoreTrades} disabled={tradesLoading}
                        style={{ padding:11, borderRadius:12, border:`1px solid ${C.panelEdge}`, background:"transparent", color:C.sage, fontSize:13, fontWeight:600, cursor:tradesLoading?"default":"pointer" }}>
                  {tradesLoading ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════════════════════════════
export default function DesktopApp() {
  // ── Onboarding state ─────────────────────────────────────────────────
  const [onboarded, setOnboarded] = useState(() => !!localStorage.getItem("ftl_onboarded"));
  const [onboardStep, setOnboardStep] = useState("intro"); // "intro" | "priming"
  const completeOnboarding = () => { localStorage.setItem("ftl_onboarded", "1"); setOnboarded(true); };

  // ── Walkthrough state ────────────────────────────────────────────────
  const [walkthroughSeen, setWalkthroughSeen] = useState(() => !!localStorage.getItem("ftl_walkthrough_seen"));
  const completeWalkthrough = () => { localStorage.setItem("ftl_walkthrough_seen", "1"); setWalkthroughSeen(true); };

  // ── Auth state ────────────────────────────────────────────────────────
  const [authState, setAuthState] = useState(() => {
    const { accessToken, email } = getStoredTokens();
    if (accessToken) return { status: "checking", email };
    return { status: "loggedOut", email: null };
  });
  // "login" | "register" — the landing page's sign-up buttons link here with ?signup.
  const [authScreen, setAuthScreen] = useState(() =>
    new URLSearchParams(window.location.search).has("signup") ? "register" : "login");
  const [checkoutCancelled, setCheckoutCancelled] = useState(false);

  // ── Subscription state ────────────────────────────────────────────────
  const [subStatus, setSubStatus] = useState(null); // null = not yet checked
  const refreshSubStatus = useCallback(async () => {
    const sub = await fetchSubscriptionStatus();
    setSubStatus(sub);
  }, []);

  // ── App state ─────────────────────────────────────────────────────────
  const [tab,      setTab]     = useState("alerts");
  const [favSet,   setFavSet]  = useState(() => new Set());
  const [traders,  setTraders] = useState(MOCK_TRADERS.map(normaliseTrader));
  const [detail,   setDetail]  = useState(null);
  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);
  const [topN,     setTopN]    = useState(RANK_MAX);
  const [favBypassRank, setFavBypassRank] = useState(true);
  const [rankWindow, setRankWindow] = useState("day");
  const rankCtl = { topN, setTopN, favBypassRank, setFavBypassRank, rankWindow, setRankWindow };

  // Shared by the mount-time auth check and handleLogin so a fresh login
  // applies saved settings the same way a page reload with an existing
  // token does, instead of silently sitting on SETTINGS_DEFAULTS.
  const applyDeviceSettings = useCallback((sData) => {
    if (!sData) return;
    setSettings({
      conviction: sData.conviction_multiplier ?? SETTINGS_DEFAULTS.conviction,
      winFloor:   sData.win_rate_floor        ?? SETTINGS_DEFAULTS.winFloor,
      favBypass:  sData.fav_bypass_filters    ?? SETTINGS_DEFAULTS.favBypass,
      notif:      sData.notif_enabled         ?? SETTINGS_DEFAULTS.notif,
      quiet:      !!(sData.quiet_from),
      from:       sData.quiet_from            ?? SETTINGS_DEFAULTS.from,
      to:         sData.quiet_to              ?? SETTINGS_DEFAULTS.to,
      whaleUsd:     sData.whale_usd_threshold     ?? SETTINGS_DEFAULTS.whaleUsd,
      dormancyDays: sData.dormancy_threshold_days ?? SETTINGS_DEFAULTS.dormancyDays,
      excludeWhale:   (sData.excluded_categories ?? "").split(",").includes("whale"),
      excludeDormant: (sData.excluded_categories ?? "").split(",").includes("dormant_wallet"),
    });
    if (sData.top_n)                   setTopN(sData.top_n);
    if (sData.fav_bypass_rank != null) setFavBypassRank(sData.fav_bypass_rank);
    if (sData.rank_window)             setRankWindow(sData.rank_window);
  }, []);

  // Unread count is owned by AlertsPane and reported up via onUnreadChange
  const [globalUnread, setGlobalUnread] = useState(0);

  // wsStatus similarly owned by AlertsPane (it holds the actual WebSocket
  // connection) and reported up via onWsStatusChange. The socket only
  // exists while AlertsPane is mounted (tab === "alerts"), so treat it as
  // "offline" on any other tab rather than showing a stale last-known value.
  const [alertsWsStatus, setAlertsWsStatus] = useState("offline");

  // ── Check auth + sub on mount ─────────────────────────────────────────
  useEffect(() => {
    const { accessToken, email } = getStoredTokens();
    if (!accessToken) { setAuthState({ status: "loggedOut", email: null }); return; }

    // Verify token is still valid and check subscription
    (async () => {
      try {
        const { data: me, error } = await apiFetch("/auth/me");
        if (error || !me) {
          // Try refresh
          const refreshed = await tryRefreshToken();
          if (!refreshed) { clearTokens(); setAuthState({ status:"loggedOut", email:null }); return; }
          if (refreshed === "kicked") { clearTokens(); setAuthState({ status:"kicked", email:null }); return; }
        }
        const { email: verifiedEmail } = getStoredTokens();
        setAuthState({ status: "loggedIn", email: me?.email ?? verifiedEmail });

        // Check subscription
        const sub = await fetchSubscriptionStatus();
        setSubStatus(sub);

        // Register device + load settings
        await registerDevice();
        const sData = await fetchDeviceSettings();
        applyDeviceSettings(sData);

        // Load leaderboard for Favorites tab
        fetchLeaderboard("day").then(({ data }) => setTraders(data));
      } catch {
        clearTokens();
        setAuthState({ status: "loggedOut", email: null });
      }
    })();
  }, [applyDeviceSettings]);

  // ── Stripe success redirect ───────────────────────────────────────────
  useEffect(() => {
    // Stripe Checkout returns to /?payment=success|cancelled (see
    // stripe_payments.py create_checkout_session).
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (payment === "success") {
      // Re-check subscription after returning from Stripe
      fetchSubscriptionStatus().then((sub) => setSubStatus(sub));
    } else if (payment === "cancelled") {
      setCheckoutCancelled(true);
    }
    if (payment || params.has("signup")) {
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleLogin = useCallback(() => {
    const { email } = getStoredTokens();
    setAuthState({ status: "loggedIn", email });
    // Kick off sub check
    fetchSubscriptionStatus().then((sub) => setSubStatus(sub));
    registerDevice();
    fetchDeviceSettings().then(applyDeviceSettings);
    fetchLeaderboard("day").then(({ data }) => setTraders(data));
  }, [applyDeviceSettings]);

  const handleLogout = useCallback(async () => {
    await authLogout();
    setAuthState({ status: "loggedOut", email: null });
    setSubStatus(null);
  }, []);

  const toggleFav = (id) => setFavSet((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openTrader = (t) => setDetail(t);
  const closeDetail = () => setDetail(null);

  const changeTab = (k) => { setTab(k); setDetail(null); };

  // ── Render gate: onboarding ────────────────────────────────────────────
  if (!onboarded) {
    return onboardStep === "intro"
      ? <OnboardingIntroScreen onContinue={() => setOnboardStep("priming")} />
      : <OnboardingPrimingScreen onContinue={completeOnboarding} sampleAlert={normaliseAlert(MOCK_ALERTS[0])} />;
  }

  // ── Render gate: auth ─────────────────────────────────────────────────
  if (authState.status === "checking") {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (authState.status === "loggedOut" || authState.status === "kicked") {
    return (
      <>
        {authScreen === "login"
          ? <LoginScreen
              onLogin={handleLogin}
              onSwitchToRegister={() => setAuthScreen("register")}
              kickedMessage={authState.status === "kicked"
                ? "Your account was signed in on another device. Sign in again to continue."
                : null}
            />
          : <RegisterScreen onRegister={handleLogin} onSwitchToLogin={() => setAuthScreen("login")} />}
        <style>{GLOBAL_CSS}</style>
      </>
    );
  }

  // ── Render gate: subscription ─────────────────────────────────────────
  const hasAccess = subStatus == null  // not yet loaded — optimistic: show app
    || subStatus.has_access;

  if (subStatus !== null && !hasAccess) {
    return (
      <>
        <PaywallScreen email={authState.email} onLogout={handleLogout} checkoutCancelled={checkoutCancelled}
          trialEligible={subStatus?.trial_eligible !== false} />
        <style>{GLOBAL_CSS}</style>
      </>
    );
  }

  // ── Render gate: walkthrough ────────────────────────────────────────────
  if (!walkthroughSeen) {
    return (
      <>
        <WalkthroughScreen onDone={completeWalkthrough} sampleAlert={normaliseAlert(MOCK_ALERTS[0])} />
        <style>{GLOBAL_CSS}</style>
      </>
    );
  }

  // ── Signed in + subscribed: main app ──────────────────────────────────
  const { accessToken } = getStoredTokens();

  return (
    <div style={{ display:"flex", height:"100vh", overflow:"hidden", background:C.bg, color:C.text, fontFamily:FONT, position:"relative" }}>
      {/* Ambient corner accents — non-green pops of color, purely decorative,
          sit behind everything else (zIndex:0 vs. zIndex:1 on Sidebar/main). */}
      <div aria-hidden="true" style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:0, overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-180, left:-180, width:420, height:420, borderRadius:"50%", background:C.accentBlue, opacity:0.10, filter:"blur(120px)" }} />
        <div style={{ position:"absolute", bottom:-200, right:-200, width:480, height:480, borderRadius:"50%", background:C.accentViolet, opacity:0.09, filter:"blur(140px)" }} />
      </div>

      <Sidebar
        activeTab={tab}
        onTab={changeTab}
        unread={globalUnread}
        wsStatus={tab === "alerts" ? alertsWsStatus : "offline"}
        email={authState.email}
        onLogout={handleLogout}
      />

      <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", position:"relative", zIndex:1 }}>
        <div style={{ flex:1, overflow:"hidden", display:"flex" }}>
          {/* Main pane */}
          <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
            {tab === "alerts"      && <AlertsPane favSet={favSet} rankCtl={rankCtl} settings={settings} accessToken={accessToken} onSelectTrader={openTrader} onUnreadChange={setGlobalUnread} onWsStatusChange={setAlertsWsStatus} />}
            {tab === "whale"       && <CategoryAlertsPane category="whale" title="Whale" emptyText="No whale-sized trades yet. Trades over the whale threshold from any tracked trader show up here, regardless of your regular alert settings." favSet={favSet} accessToken={accessToken} onSelectTrader={openTrader} />}
            {tab === "dormant"     && <CategoryAlertsPane category="dormant_wallet" title="Dormant Wallet" emptyText="No dormant-wallet reactivations yet. Trades from wallets that had gone quiet before suddenly trading again show up here, regardless of your regular alert settings." favSet={favSet} accessToken={accessToken} onSelectTrader={openTrader} />}
            {tab === "leaderboard" && <LeaderboardPane favSet={favSet} toggleFav={toggleFav} onSelectTrader={openTrader} rankCtl={rankCtl} />}
            {tab === "favorites"   && <FavoritesPane favSet={favSet} toggleFav={toggleFav} onSelectTrader={openTrader} traders={traders} />}
            {tab === "settings"    && <SettingsPane favSet={favSet} toggleFav={toggleFav} rankCtl={rankCtl} settings={settings} setSettings={setSettings} email={authState.email} traders={traders} subStatus={subStatus} refreshSubStatus={refreshSubStatus} onReplayWalkthrough={() => setWalkthroughSeen(false)} />}
          </div>

          {/* Right detail panel — slides in without losing tab context */}
          {detail && (
            <TraderDetailPanel
              trader={detail}
              favSet={favSet}
              toggleFav={toggleFav}
              onClose={closeDetail}
              settings={settings}
            />
          )}
        </div>
      </div>

      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

// ── Global CSS ────────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; background: ${C.bg}; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${C.panelEdge}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${C.panelEdge2}; }
  @keyframes spin  { to { transform: rotate(360deg); } }
  @keyframes ftlp  { 0%{transform:scale(1);opacity:.7} 70%{transform:scale(1.5);opacity:0} 100%{opacity:0} }
  @keyframes wsPulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  button:focus-visible, a:focus-visible, input:focus-visible, [role="button"]:focus-visible, [role="switch"]:focus-visible {
    outline: 2px solid ${C.green}; outline-offset: 2px;
  }
  input[type=range] { height: 4px; }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation: none !important; transition: none !important; }
  }
`;
